// src/pages/AdminFeedbackPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../api/axios';
import { QRCodeSVG } from 'qrcode.react';
import { useUser } from '../context/UserContext';
import * as XLSX from 'xlsx';

// --- QR 코드 팝업(Modal) 컴포넌트 ---
const QrModal = ({ url, title, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50" onClick={onClose}>
    <div className="bg-white p-8 rounded-lg text-center shadow-2xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
      <h2 className="text-2xl font-bold mb-6">{title}</h2>
      
      {/* QR Code - 가운데 정렬 */}
      <div className="flex justify-center mb-6">
      <QRCodeSVG value={url} size={256} />
      </div>
      
      <p className="text-gray-600 mb-4">Scan this code or click the button below to open the feedback form.</p>
      
      {/* 버튼으로 링크 이동 */}
      <div className="space-y-3">
        <button 
          onClick={() => window.open(url, '_blank')}
          className="w-full py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors"
        >
          🔗 Open Feedback Form
        </button>
        <button 
          onClick={onClose} 
          className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

export default function AdminFeedbackPage() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('summary'); // 'summary', 'games', 'past', 'staff'
  const [selectedBranch, setSelectedBranch] = useState('all'); // 'all', 'NCCU', 'NTU', 'Taipei'
  const [selectedDateRange, setSelectedDateRange] = useState('all'); // 'all', 'today', 'week', 'month', 'year'
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]); // 피드백이 있는 이벤트들
  const [allAvailableEvents, setAllAvailableEvents] = useState([]); // 모든 이벤트 (Create 모달용)
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedCurrentEventId, setSelectedCurrentEventId] = useState(''); // Current Events 탭용
  const [selectedPastEventId, setSelectedPastEventId] = useState(''); // Past Events 탭용
  const [movedToPast, setMovedToPast] = useState(new Set()); // Past로 이동된 이벤트 ID들
  const [showPerformanceCriteria, setShowPerformanceCriteria] = useState(false); // Performance 기준 표시 여부
  const [summary, setSummary] = useState(null);
  const [gameRows, setGameRows] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [newFeedbackForm, setNewFeedbackForm] = useState({
    eventId: '',
    games: []
  });
  const [allGames, setAllGames] = useState([]);
  const [showQrModal, setShowQrModal] = useState(null); // { event, role }
  const [selectedPastEvents, setSelectedPastEvents] = useState(new Set());
  const [aggregatedData, setAggregatedData] = useState(null);
  const [staffFeedback, setStaffFeedback] = useState({ self: [], peer: [], mostHelpful: [] });
  const [feedbackDetails, setFeedbackDetails] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameDetails, setGameDetails] = useState([]);
  const [showGameDetails, setShowGameDetails] = useState(false);
  
  // showGameDetails 상태 변경
  useEffect(() => {
    // Silent
  }, [showGameDetails]);

  // UserContext에서 userRole 가져오기
  const userRole = user?.role || 'member';

  // 날짜 필터링 함수
  const getDateRange = (range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (range) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return { start: weekAgo, end: today };
      case 'month':
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return { start: monthAgo, end: today };
      case '3months':
        const threeMonthsAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
        return { start: threeMonthsAgo, end: today };
      case '6months':
        const sixMonthsAgo = new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000);
        return { start: sixMonthsAgo, end: today };
      case 'year':
        const yearAgo = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
        return { start: yearAgo, end: today };
      case 'custom':
        return {
          start: customDateRange.start ? new Date(customDateRange.start) : null,
          end: customDateRange.end ? new Date(customDateRange.end) : null
        };
      default:
        return null;
    }
  };

  // 이벤트가 날짜 범위에 포함되는지 확인
  const isEventInDateRange = (event, dateRange) => {
    if (!dateRange || !event.date) return true;
    
    const eventDate = new Date(event.date);
    const { start, end } = dateRange;
    
    if (start && eventDate < start) return false;
    if (end && eventDate >= end) return false;
    
    return true;
  };

  // 지부 및 날짜 필터링 로직
  const filteredEvents = useMemo(() => {
    const eventsToFilter = activeTab === 'past' ? pastEvents : events;
    let filtered = eventsToFilter;
    
    // 지부 필터링
    if (selectedBranch !== 'all') {
      filtered = filtered.filter(event => event.branch === selectedBranch);
    }
    
    // 날짜 필터링
    const dateRange = getDateRange(selectedDateRange);
    if (dateRange) {
      filtered = filtered.filter(event => isEventInDateRange(event, dateRange));
    }
    
    return filtered;
  }, [selectedBranch, selectedDateRange, customDateRange, events, pastEvents, activeTab]);

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // URL 파라미터 처리 - eventId와 autoOpen 파라미터 확인
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const eventIdFromUrl = urlParams.get('eventId');
    const autoOpen = urlParams.get('autoOpen') === 'true';
    const tab = urlParams.get('tab'); // 새로운 tab 파라미터 추가
    
    if (eventIdFromUrl && autoOpen) {
      // 이벤트 데이터가 로드된 후에 처리
      setTimeout(() => {
        // Staff Feedback 탭으로 이동하는 경우
        if (tab === 'staff') {
          setActiveTab('staff');
          setSelectedEventId(eventIdFromUrl);
          loadStaffFeedback(eventIdFromUrl);
          return;
        }
        
        // 이벤트가 Past Feedback에 있는지 확인
        const isInPastEvents = pastEvents.some(event => 
          (event.id || event.eventId) == eventIdFromUrl
        );
        
        if (isInPastEvents) {
          // Past Feedback에 있으면 해당 탭으로 전환
          setActiveTab('past');
          // Past Events에서 해당 이벤트 선택
          setSelectedPastEvents(new Set([eventIdFromUrl]));
        } else {
          // Current Events에 있으면 Current 탭 유지
          setActiveTab('summary');
        }
        
        // 이벤트 선택 및 피드백 상세 정보 로드
        setSelectedEventId(eventIdFromUrl);
        loadFeedbackDetails(eventIdFromUrl);
      }, 1500); // 이벤트 데이터 로드를 위한 지연 (Past Events 로딩 시간 고려)
    }
  }, [pastEvents, events]); // pastEvents와 events가 로드된 후에 실행

  useEffect(() => {
    // Load events with feedback from backend
    axios.get('/api/feedback/events').then(res => {
      const eventsWithFeedback = res.data || [];
      setAllEvents(eventsWithFeedback); // 피드백이 있는 이벤트들
    }).catch(() => {
      setAllEvents([]);
    });
    
    // Load all events for feedback creation (Upcoming Events 기준)
    axios.get('/api/admin/events').then(res => {
      const allEvents = res.data || [];
      
      // Upcoming Events 기준: archived되지 않은 이벤트만 (이벤트 관리 페이지와 동일)
      const upcomingEvents = allEvents.filter(e => !e.archived);
      setAllAvailableEvents(upcomingEvents);
    }).catch(err => {
      setAllAvailableEvents([]);
    });
    
    // 서버에서 archived된 피드백 상태를 가져와서 movedToPast 초기화
    axios.get('/api/feedback/archived-events').then(res => {
      const archivedEventIds = res.data || [];
      if (archivedEventIds.length > 0) {
        setMovedToPast(new Set(archivedEventIds));
      }
    }).catch(err => {
      // Silent fail
    });
    
    // Load all games for event assignment
    axios.get('/api/admin/games').then(res => {
      setAllGames(res.data || []);
    }).catch(err => {
      setAllGames([]);
    });
  }, []);

  // movedToPast 상태가 변경될 때마다 이벤트 분류 (간단한 방법으로 복원)
  useEffect(() => {
    if (allEvents.length > 0) {
      const upcoming = allEvents.filter(event => {
        const eventId = event.id || event.eventId;
        const isMoved = movedToPast.has(eventId);
        return !isMoved;
      });
      
      const past = allEvents.filter(event => {
        const eventId = event.id || event.eventId;
        return movedToPast.has(eventId);
      });
      
      setEvents(upcoming);
      setPastEvents(past);
    }
  }, [movedToPast, allEvents]);

  useEffect(() => {
    if (!selectedEventId) { setSummary(null); setGameRows([]); return; }
    
    axios.get(`/api/feedback/event/${selectedEventId}/summary`)
      .then(res => {
        setSummary(res.data);
      })
      .catch(error => {
        setSummary(null);
      });
      
    axios.get(`/api/feedback/event/${selectedEventId}/games`)
      .then(res => {
        setGameRows(res.data || []);
      })
      .catch(error => {
        setGameRows([]);
      });
  }, [selectedEventId]);

  // QR 코드 URL 생성 로직
  const generateQrUrl = (event, role) => {
    // Staff 폼의 경우 새로운 Staff Feedback Form으로 연결
    if (role === 'staff') {
      const url = `${window.location.origin}/feedback/staff/${event.id || event.eventId}?branch=${event.branch}`;
      setShowQrModal({ url, title: `${event.title || event.eventTitle} (Staff Evaluation)` });
    } else {
      const url = `${window.location.origin}/feedback/event/${event.id || event.eventId}?branch=${event.branch}&role=${role}`;
      setShowQrModal({ url, title: `${event.title || event.eventTitle} (Member Feedback)` });
    }
  };

  // Create New Feedback 처리 - 피드백 폼 생성
  const handleCreateFeedback = async () => {
    if (!newFeedbackForm.eventId || newFeedbackForm.games.length === 0) {
      alert('Please select an event and at least one game.');
      return;
    }
    
    try {
      // 피드백 폼 생성 (이벤트에 게임 할당)
      await axios.post(`/api/admin/events/${newFeedbackForm.eventId}/games`, newFeedbackForm.games);
      alert('Feedback form created successfully! Games have been assigned to the event.');
      
      // 모달 닫기 및 폼 초기화
      setShowCreateModal(false);
      setNewFeedbackForm({ eventId: '', games: [] });
      
      // 모든 이벤트 데이터를 다시 로드하여 UI 업데이트
      await Promise.all([
        // Current Events 다시 로드
        axios.get('/api/admin/events').then(res => setEvents(res.data || [])),
        // Past Events 다시 로드 (archived-events 사용)
        axios.get('/api/feedback/archived-events').then(res => setPastEvents(res.data || [])),
        // 피드백이 있는 이벤트 목록 다시 로드
        axios.get('/api/feedback/events').then(res => setAllEvents(res.data || []))
      ]);
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.response?.data || err?.message || 'Unknown error';
      alert('Failed to create feedback form: ' + errorMessage);
    }
  };

  // XLSX 다운로드 함수
  const downloadXLSX = () => {
    if (selectedPastEvents.size === 0) {
      alert('Please select events to download.');
      return;
    }

    // 선택된 이벤트들의 피드백 데이터 수집
    const selectedEventsData = pastEvents.filter(event => 
      selectedPastEvents.has(event.id || event.eventId)
    );

    // XLSX 헤더
    const headers = [
      'Event ID',
      'Event Title', 
      'Branch',
      'Date',
      'Total Submissions',
      'Overall Average',
      'NPS Average',
      'Response Rate',
      'Top Activities',
      'Comments'
    ];

    // XLSX 데이터 생성
    const data = selectedEventsData.map(event => {
      const eventSummary = eventSummaries[event.id || event.eventId];
      return [
        event.id || event.eventId,
        event.eventTitle || 'N/A',
        event.branch || 'N/A',
        event.date || 'N/A',
        eventSummary?.count || 0,
        eventSummary?.overallAvg?.toFixed(1) || 'N/A',
        eventSummary?.npsAvg?.toFixed(1) || 'N/A',
        eventSummary?.responseRate?.toFixed(1) + '%' || 'N/A',
        eventSummary?.top3Activities?.join('; ') || 'N/A',
        eventSummary?.comments?.join('; ') || 'N/A'
      ];
    });

    // 워크시트 생성
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    
    // 워크북 생성
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedback Summary');
    
    // 파일 다운로드
    XLSX.writeFile(wb, `feedback_data_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // 이벤트를 Past feedback으로 이동
  const handleMoveToPast = async (eventId) => {
    if (!window.confirm('Are you sure you want to move this event to Past Feedback? This will archive the event.')) {
      return;
    }
    
    // allEvents에서 해당 이벤트를 찾아서 일관된 ID 사용
    const targetEvent = allEvents.find(event => 
      (event.id && event.id.toString() === eventId.toString()) || 
      (event.eventId && event.eventId.toString() === eventId.toString())
    );
    
    if (!targetEvent) {
      alert('Event not found. Please try again.');
      return;
    }
    
    // 일관된 ID 사용 (event.id || event.eventId)
    const consistentId = targetEvent.id || targetEvent.eventId;
    
    try {
      // 피드백만 Past Feedback으로 이동 (이벤트 아카이브 제거)
      await axios.post('/api/feedback/move-to-past', { eventId: consistentId });
      
      // 로컬 상태에서 movedToPast에 추가 (새로고침 시에도 유지되도록)
      setMovedToPast(prev => {
        const newSet = new Set([...prev, consistentId]);
        return newSet;
      });
      
      alert('Feedback moved to Past Feedback successfully!');
    } catch (error) {
      alert('Failed to move feedback to Past. Please try again.');
    }
  };

  const handleRestoreToCurrent = async (eventId) => {
    if (!window.confirm('Are you sure you want to restore this event to Current Feedback? This will unarchive the feedback.')) {
      return;
    }
    
    try {
      await axios.post('/api/feedback/restore', { eventId });
      
      // 로컬 상태에서 movedToPast에서 제거
      setMovedToPast(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(eventId);
        return newSet;
      });
      
      alert('Feedback restored to Current Feedback successfully!');
    } catch (error) {
      alert('Failed to restore feedback to Current. Please try again.');
    }
  };

  // 피드백 삭제 처리
  const handleDeleteFeedback = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete all feedback for this event? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`/api/feedback/event/${eventId}`);
      alert('Feedback deleted successfully!');
      
      // 선택된 이벤트가 삭제된 경우 선택 해제
      if (selectedEventId === eventId) {
        setSelectedEventId('');
        setSummary(null);
        setGameRows([]);
        setFeedbackDetails([]);
        setShowDetails(false);
      }
      
      // 피드백이 있는 이벤트 목록을 다시 로드하여 UI 업데이트
      const res = await axios.get('/api/feedback/events');
      const eventsWithFeedback = res.data || [];
      setAllEvents(eventsWithFeedback);
    } catch (error) {
      alert('Failed to delete feedback: ' + (error.response?.data?.message || error.message));
    }
  };

  // 이벤트 삭제 처리
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This will also delete all associated feedback and cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/events/${eventId}`);
      alert('Event deleted successfully!');
      
      // 이벤트 목록에서 제거
      setEvents(prev => prev.filter(e => (e.id || e.eventId) !== eventId));
      setPastEvents(prev => prev.filter(e => (e.id || e.eventId) !== eventId));
      
      // 선택된 이벤트가 삭제된 경우 선택 해제
      if (selectedEventId === eventId) {
        setSelectedEventId('');
        setSummary(null);
        setGameRows([]);
      }
    } catch (error) {
      alert('Failed to delete event: ' + (error.response?.data?.message || error.message));
    }
  };


  // 체크박스 선택 처리
  const handleEventSelection = (eventId, isChecked) => {
    const newSelection = new Set(selectedPastEvents);
    if (isChecked) {
      newSelection.add(eventId);
    } else {
      newSelection.delete(eventId);
    }
    setSelectedPastEvents(newSelection);
  };

  // 전체 선택/해제
  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allEventIds = filteredEvents.map(e => e.id || e.eventId);
      setSelectedPastEvents(new Set(allEventIds));
    } else {
      setSelectedPastEvents(new Set());
    }
  };

  // 선택된 이벤트들의 집계 데이터 계산
  const calculateAggregatedData = async () => {
    if (selectedPastEvents.size === 0) {
      setAggregatedData(null);
      return;
    }

    try {
      const eventIds = Array.from(selectedPastEvents);
      
      // 모든 필요한 데이터를 병렬로 가져오기
      const summaryPromises = eventIds.map(id => 
        axios.get(`/api/feedback/event/${id}/summary`).catch(() => null)
      );
      const gamePromises = eventIds.map(id => 
        axios.get(`/api/feedback/event/${id}/games`).catch(() => null)
      );
      const feedbackPromises = eventIds.map(id => 
        axios.get(`/api/feedback/event/${id}/details`).catch(() => null)
      );

      const [summaries, gameData, feedbackData] = await Promise.all([
        Promise.all(summaryPromises),
        Promise.all(gamePromises),
        Promise.all(feedbackPromises)
      ]);

      // 유효한 데이터만 필터링
      const validSummaries = summaries.filter(s => s && s.data).map(s => s.data);
      const validGameData = gameData.filter(g => g && g.data).flatMap(g => g.data);
      const validFeedbackData = feedbackData.filter(f => f && f.data).flatMap(f => f.data);

      if (validSummaries.length === 0) {
        setAggregatedData(null);
        return;
      }

      // 기본 통계 계산
      const totalSubmissions = validSummaries.reduce((sum, s) => sum + (s.count || 0), 0);
      const avgOverall = validSummaries.reduce((sum, s) => sum + (s.overallAvg || 0), 0) / validSummaries.length;
      const avgResponseRate = validSummaries.reduce((sum, s) => sum + (s.responseRate || 0), 0) / validSummaries.length;
      const avgParticipantsFit = validSummaries.reduce((sum, s) => sum + (s.participantsFitAvg || 0), 0) / validSummaries.length;
      const avgInteractionOpportunity = validSummaries.reduce((sum, s) => sum + (s.interactionOpportunityAvg || 0), 0) / validSummaries.length;

      // Top 3 Activities 수집 및 집계
      const allTop3Activities = validSummaries
        .filter(s => s.top3Activities && s.top3Activities.length > 0)
        .flatMap(s => s.top3Activities);

      // Comments 수집
      const allComments = validSummaries
        .filter(s => s.comments && s.comments.length > 0)
        .flatMap(s => s.comments);

      // Good Points 수집
      const allGoodPoints = validSummaries
        .filter(s => s.goodPoints && s.goodPoints.length > 0)
        .flatMap(s => s.goodPoints);

      // Improvements 수집
      const allImprovements = validSummaries
        .filter(s => s.improvements && s.improvements.length > 0)
        .flatMap(s => s.improvements);

      // 게임별 집계 (게임 이름 포함)
      const gameAggregation = {};
      validGameData.forEach(game => {
        if (!gameAggregation[game.gameId]) {
          gameAggregation[game.gameId] = { 
            ratings: [], 
            totalResponses: 0,
            gameName: game.gameName || allGames.find(g => g.gameId === game.gameId)?.name || `Game ${game.gameId}`,
            eventIds: new Set() // 이벤트 ID들을 추적
          };
        }
        gameAggregation[game.gameId].ratings.push(Number(game.rating) || 0);
        gameAggregation[game.gameId].totalResponses += (game.responseCount || 0);
        if (game.eventId) {
          gameAggregation[game.gameId].eventIds.add(game.eventId);
        }
      });

      const aggregatedGames = Object.entries(gameAggregation).map(([gameId, data]) => ({
        gameId,
        gameName: data.gameName,
        avgRating: data.ratings.reduce((sum, r) => sum + r, 0) / data.ratings.length,
        totalResponses: data.totalResponses,
        eventCount: data.ratings.length,
        eventIds: Array.from(data.eventIds) // 이벤트 ID 배열로 변환
      }));

      setAggregatedData({
        eventCount: selectedPastEvents.size,
        totalSubmissions,
        avgOverall,
        avgResponseRate,
        avgParticipantsFit,
        avgInteractionOpportunity,
        top3Activities: allTop3Activities,
        comments: allComments,
        goodPoints: allGoodPoints,
        improvements: allImprovements,
        games: aggregatedGames,
        feedbackDetails: validFeedbackData
      });
    } catch (error) {
      setAggregatedData(null);
    }
  };

  // 선택된 이벤트가 변경될 때마다 집계 데이터 계산
  useEffect(() => {
    if (activeTab === 'past') {
      calculateAggregatedData();
    }
  }, [selectedPastEvents, activeTab]);

  // 탭이 변경될 때 선택된 이벤트 ID 초기화
  useEffect(() => {
    if (activeTab === 'summary') {
      setSelectedPastEventId('');
    } else if (activeTab === 'past') {
      setSelectedCurrentEventId('');
    }
  }, [activeTab]);

  // Staff 피드백 데이터 로드
  const loadStaffFeedback = async (eventId) => {
    try {
      // 각 API를 개별적으로 호출하여 어느 것이 실패하는지 확인
      let selfData = [];
      let peerData = [];
      let mostHelpfulData = [];
      
    try {
      const selfResponse = await axios.get(`/api/feedback/staff/self/${eventId}`);
        selfData = selfResponse.data || [];
      } catch (selfError) {
        // Silent fail
      }
      
      try {
        const peerResponse = await axios.get(`/api/feedback/staff/peer/aggregated/${eventId}`);
        peerData = peerResponse.data || [];
      } catch (peerError) {
        // Silent fail
      }
      
      try {
        const mostHelpfulResponse = await axios.get(`/api/feedback/staff/most-helpful/${eventId}`);
        mostHelpfulData = mostHelpfulResponse.data || [];
      } catch (mostHelpfulError) {
        // Silent fail
      }
      
      const staffFeedbackData = {
        self: selfData,
        peer: peerData,
        mostHelpful: mostHelpfulData
      };
      
      setStaffFeedback(staffFeedbackData);
    } catch (error) {
      setStaffFeedback({ self: [], peer: [] });
    }
  };

  // 피드백 상세 정보 로드
  const loadFeedbackDetails = async (eventId) => {
    // UI 업데이트를 즉시 실행하고 무거운 작업은 다음 이벤트 루프로 지연
    setTimeout(async () => {
      try {
        const response = await axios.get(`/api/feedback/event/${eventId}/details`);
        setFeedbackDetails(response.data || []);
        setShowDetails(true);
      } catch (error) {
        setFeedbackDetails([]);
      }
    }, 0);
  };

  // 게임 상세 정보 로드
  const loadGameDetails = async (gameId) => {
    try {
      let eventId = selectedEventId;
      
      // selectedEventId가 있으면 바로 사용
      if (!eventId) {
        // selectedEventId가 없으면 현재 표시된 데이터에서 찾기
        
        // 1. Past Events 탭의 선택된 이벤트들에서 찾기
        if (selectedPastEvents.size > 0) {
          for (const selectedEventId of selectedPastEvents) {
            try {
              const response = await axios.get(`/api/feedback/event/${selectedEventId}/games`);
              const games = response.data || [];
              const hasGame = games.some(game => game.gameId === gameId);
              if (hasGame) {
                eventId = selectedEventId;
                break;
              }
            } catch (error) {
              continue;
            }
          }
        }
        
        // 2. aggregatedData에서 찾기
        if (!eventId && aggregatedData && aggregatedData.games) {
          const gameData = aggregatedData.games.find(g => g.gameId === gameId);
          if (gameData && gameData.eventIds && gameData.eventIds.length > 0) {
            eventId = gameData.eventIds[0];
          }
        }
        
        // 3. Current Events에서 찾기
        if (!eventId) {
          const currentEvent = events.find(e => {
            const eventId = e.id || e.eventId;
            return eventId && gameRows.some(game => game.eventId === eventId);
          });
          
          if (currentEvent) {
            eventId = currentEvent.id || currentEvent.eventId;
          }
        }
      }
      
      if (!eventId) {
        alert('Event not found. Please try again.');
        return;
      }
      
      const response = await axios.get(`/api/feedback/event/${eventId}/game/${gameId}/details`);
      setGameDetails(response.data || []);
      setSelectedGame(gameId);
      setShowGameDetails(true);
    } catch (error) {
      setGameDetails([]);
      alert('Failed to load game details. Please try again.');
    }
  };

  // XLSX 다운로드 함수
  const downloadFeedbackXLSX = async (data) => {
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }

    // 선택된 게임만 가져오기 (gameRows에서)
    let gameIds = new Set();
    
    // gameRows에서 실제로 선택된 게임 ID만 수집
    if (gameRows && gameRows.length > 0) {
      gameRows.forEach(game => {
        if (game.gameId) {
          gameIds.add(game.gameId);
        }
      });
    }
    
    // gameRows가 없으면 피드백 데이터에서 게임 ID 수집
    if (gameIds.size === 0) {
      data.forEach(feedback => {
        if (feedback.gameRatings && typeof feedback.gameRatings === 'object') {
          Object.keys(feedback.gameRatings).forEach(gameId => {
            if (gameId && gameId !== 'null' && gameId !== 'undefined') {
              gameIds.add(gameId);
            }
          });
        }
      });
    }

    // XLSX 헤더 생성
    const headers = [
      'Response #',
      // Step 1: 기본 평점
      'Overall Rating',
      'Participants Fit',
      'Interaction Opportunity',
      // Step 1 추가 질문들
      'Leadership Interest',
      'Leadership Instagram ID',
      'Reels Participation',
      'Reels Instagram ID',
      // Step 2: 게임별 평점 및 피드백
      ...Array.from(gameIds).flatMap(gameId => [
        `Game ${gameId} Rating`,
        `Game ${gameId} Positive`,
        `Game ${gameId} Negative`,
        `Game ${gameId} Own Feedback`
      ]),
      // Step 3: 활동
      'Top 3 Activities',
      'What they liked most (6)',
      'Suggestions for improvement (6)',
      'Submitted Date'
    ];

    // 각 피드백별 게임 데이터 가져오기
    const xlsxData = await Promise.all(data.map(async (feedback, index) => {
      const row = [
        index + 1,
        // Step 1: 기본 평점
        feedback.overall || '',
        feedback.participantsFit || '',
        feedback.interactionOpportunity || '',
        // Step 1 추가 질문들
        feedback.leadershipInterest && feedback.leadershipInterest.startsWith('yes') ? 'Yes' : 'No',
        feedback.leadershipInterest && feedback.leadershipInterest.includes(':') ? feedback.leadershipInterest.split(':')[1] : '',
        feedback.reelsParticipation && feedback.reelsParticipation.startsWith('yes') ? 'Yes' : 'No',
        feedback.reelsParticipation && feedback.reelsParticipation.includes(':') ? feedback.reelsParticipation.split(':')[1] : '',
      ];

      // Step 2: 게임별 데이터 (Step 3과 같은 방식으로 처리)
      for (const gameId of Array.from(gameIds)) {
        let rating = '';
        let positiveOptions = [];
        let negativeOptions = [];
        let ownFeedback = '';
        
        // Step 3과 같은 방식으로 직접 접근
        if (feedback.gameRatings && feedback.gameRatings[gameId]) {
          rating = feedback.gameRatings[gameId];
        }
        
        if (feedback.gameNotes && feedback.gameNotes[gameId]) {
          const gameNote = feedback.gameNotes[gameId];
          
          if (Array.isArray(gameNote)) {
            // Positive/Negative 옵션과 Custom Feedback 분리
            const positiveOptionsList = ['Fun & engaging', 'Helped me connect', 'Right duration'];
            const negativeOptionsList = ['Boring', 'Group size issue (too many/few)', 'Confusing / unclear'];
            
            gameNote.forEach(note => {
              if (positiveOptionsList.some(option => note.includes(option))) {
                positiveOptions.push(note);
              } else if (negativeOptionsList.some(option => note.includes(option))) {
                negativeOptions.push(note);
              } else {
                // 옵션이 아닌 실제 커스텀 피드백
                ownFeedback = note;
              }
            });
          }
        }

        // Step 3과 같은 방식으로 데이터 추가
        row.push(
          rating || '',
          positiveOptions.join('; ') || '',
          negativeOptions.join('; ') || '',
          ownFeedback || ''
        );
      }

      // Step 3: 활동
      row.push(
        feedback.top3Activities || '',
        feedback.goodPoints || '',
        feedback.improvements || '',
        new Date(feedback.createdAt).toLocaleDateString()
      );

      return row;
    }));

    // 워크시트 생성
    const ws = XLSX.utils.aoa_to_sheet([headers, ...xlsxData]);
    
    // 워크북 생성
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedback Details');
    
    // 파일 다운로드
    XLSX.writeFile(wb, `feedback_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
      {showQrModal && <QrModal url={showQrModal.url} title={showQrModal.title} onClose={() => setShowQrModal(null)} />}
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Feedback Dashboard</h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage and analyze member feedback across all events</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium shadow-sm">
          + Create New Feedback
        </button>
      </div>

      {/* 탭 메뉴 */}
      <div className="border-b border-gray-200 mb-8">
        <nav className={`-mb-px flex gap-2 sm:gap-6 ${isMobile ? 'flex-col sm:flex-row' : ''}`}>
          <button onClick={() => setActiveTab('summary')} className={`py-3 sm:py-4 px-3 border-b-2 font-medium text-sm sm:text-base rounded-t-lg transition-all duration-200 ${isMobile ? 'w-full text-center' : ''} ${activeTab === 'summary' ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-transparent text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-25'}`}>
            📋 Current Events
          </button>
          <button onClick={() => setActiveTab('past')} className={`py-3 sm:py-4 px-3 border-b-2 font-medium text-sm sm:text-base rounded-t-lg transition-all duration-200 ${isMobile ? 'w-full text-center' : ''} ${activeTab === 'past' ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-transparent text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-25'}`}>
            📚 Past Feedback
          </button>
          <button onClick={() => setActiveTab('staff')} className={`py-3 sm:py-4 px-3 border-b-2 font-medium text-sm sm:text-base rounded-t-lg transition-all duration-200 ${isMobile ? 'w-full text-center' : ''} ${activeTab === 'staff' ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-transparent text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-25'}`}>
            👥 Staff Feedback
          </button>
        </nav>
      </div>
      
      <div className="mt-8">
        {activeTab === 'summary' && (
          <div>
            <div className="mb-6 flex gap-4 flex-row">
              <div>
              <label htmlFor="branch-filter" className="block text-sm font-medium text-gray-700">Filter by Branch</label>
              <select 
                id="branch-filter"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="mt-1 block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Branches</option>
                <option value="NCCU">NCCU</option>
                <option value="NTU">NTU</option>
                <option value="Taipei">Taipei</option>
              </select>
              </div>
              <div>
                <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700">Filter by Date</label>
                <select 
                  id="date-filter"
                  value={selectedDateRange}
                  onChange={(e) => {
                    setSelectedDateRange(e.target.value);
                    if (e.target.value !== 'custom') {
                      setShowCustomDatePicker(false);
                    }
                  }}
                  className="mt-1 block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">1 Week</option>
                  <option value="month">1 Month</option>
                  <option value="3months">3 Months</option>
                  <option value="6months">6 Months</option>
                  <option value="year">1 Year</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              {selectedDateRange === 'custom' && (
                <div className="flex gap-2 items-end">
                  <div>
                    <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">From</label>
                    <input
                      type="date"
                      id="start-date"
                      value={customDateRange.start}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    />
                  </div>
                  <div>
                    <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">To</label>
                    <input
                      type="date"
                      id="end-date"
                      value={customDateRange.end}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Event Feedback List</h2>
              <ul className="space-y-4">
                {filteredEvents.map((event, index) => (
                  <li key={`filtered-event-${event.eventId || event.id || index}-${event.eventTitle || 'untitled'}`} className="p-4 border rounded-lg sm:flex justify-between items-center space-y-4 sm:space-y-0">
                    <div className="flex-grow">
                      <p className="font-bold text-lg text-gray-900">{event.eventTitle}</p>
                      <p className="text-sm text-gray-600"><span className="font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-full text-xs">{event.branch}</span> | {new Date(event.eventDateTime || event.date).toLocaleString()}</p>
                    </div>
                    <div className={`flex gap-2 flex-shrink-0 ${isMobile ? 'flex-col' : 'flex-row'}`}>
                      <button onClick={() => generateQrUrl(event, 'member')} className={`text-sm bg-slate-100 text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-200 transition-colors duration-200 ${isMobile ? 'w-full' : ''}`}>Member QR</button>
                      <button onClick={() => generateQrUrl(event, 'staff')} className={`text-sm bg-slate-100 text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-200 transition-colors duration-200 ${isMobile ? 'w-full' : ''}`}>Staff QR</button>
                      <button onClick={() => {
                        const eventId = event.id || event.eventId;
                        if (selectedCurrentEventId === eventId) {
                          // 이미 선택된 이벤트를 다시 클릭하면 닫기
                          setSelectedCurrentEventId('');
                          setSelectedEventId('');
                        } else {
                          // 새로운 이벤트 선택
                          setSelectedCurrentEventId(eventId);
                          setSelectedEventId(eventId); // 기존 로직 유지
                          loadFeedbackDetails(eventId);
                        }
                      }} className={`text-sm bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors duration-200 font-medium ${isMobile ? 'w-full' : ''}`}>View Details</button>
                      {(userRole?.toLowerCase() === 'leader' || userRole?.toLowerCase() === 'president' || userRole?.toLowerCase() === 'admin') && (
                        <>
                      <button onClick={() => handleMoveToPast(event.id || event.eventId)} className={`text-sm bg-amber-100 text-amber-700 px-3 py-2 rounded-lg hover:bg-amber-200 transition-colors duration-200 font-medium ${isMobile ? 'w-full' : ''}`}>Move to Past</button>
                      <button onClick={() => handleDeleteFeedback(event.id || event.eventId)} className={`text-sm bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors duration-200 font-medium ${isMobile ? 'w-full' : ''}`}>Delete Feedback</button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {selectedCurrentEventId && (
              <div className="bg-white p-6 rounded-lg shadow-md mt-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2">📊</span>
                  Event Feedback Summary (#{selectedCurrentEventId})
                </h3>
                {summary ? (
                  <div>
                    {/* Enhanced Summary Cards */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
                      <h4 className="font-semibold mb-4 flex items-center">
                        <span className="mr-2">📊</span>
                        Key Metrics
                      </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center mb-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Total Submissions</div>
                        <div className="text-2xl font-bold text-blue-600">{summary.count}</div>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Overall Rating</div>
                        <div className="text-2xl font-bold text-yellow-500">{Number(summary.overallAvg).toFixed(1)} ★</div>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Response Rate</div>
                        <div className="text-2xl font-bold text-purple-600">
                          {summary.responseRate !== undefined ? `${summary.responseRate.toFixed(1)}%` : 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Additional Metrics */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <div className="text-sm text-gray-500">Participants Fit</div>
                          <div className="text-xl font-bold text-gray-700">
                            {summary.participantsFitAvg ? Number(summary.participantsFitAvg).toFixed(1) : 'N/A'} ★
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <div className="text-sm text-gray-500">Interaction Opportunity</div>
                          <div className="text-xl font-bold text-gray-700">
                            {summary.interactionOpportunityAvg ? Number(summary.interactionOpportunityAvg).toFixed(1) : 'N/A'} ★
                            </div>
                          </div>
                        </div>
                    </div>


                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">📋</div>
                    <p className="text-gray-500">No feedback data available yet.</p>
                  </div>
                )}

                {/* Game Ratings Section */}
                <div className="mt-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <span className="mr-2">🎮</span>
                    Game Ratings
                  </h4>
                  {gameRows.length > 0 ? (
                    isMobile ? (
                      // Mobile Card View for Game Ratings
                      <div className="space-y-4">
                        {gameRows.map((g,i) => {
                          const gameName = allGames.find(game => game.gameId === g.gameId)?.name || g.gameId;
                          const rating = Number(g.rating);
                          const performance = rating >= 4.5 ? '🔥 Excellent' : rating >= 4.0 ? '👍 Good' : rating >= 3.5 ? '👌 Average' : '⚠️ Needs Improvement';
                          
                          return (
                            <div key={i} className="bg-gray-50 p-4 rounded-lg border">
                              <div className="space-y-3">
                                <div className="flex justify-between items-start">
                                  <h5 className="font-semibold text-gray-900">{gameName}</h5>
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg font-bold text-blue-600">{rating.toFixed(1)}</span>
                                    <span className="text-sm text-gray-500">★</span>
                                  </div>
                                </div>
                                
                                {g.customFeedbackCount && (
                                  <div className="bg-white p-3 rounded border">
                                    <p className="text-sm text-gray-700">Custom Feedback: {g.customFeedbackCount}</p>
                                  </div>
                                )}
                                
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-600">Performance:</span>
                                    <button 
                                      onClick={() => setShowPerformanceCriteria(!showPerformanceCriteria)}
                                      className="text-blue-500 hover:text-blue-700 text-sm"
                                      title="Rating Criteria"
                                    >
                                      ℹ️
                                    </button>
                                  </div>
                                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    rating >= 4.5 ? 'bg-emerald-100 text-emerald-700' :
                                    rating >= 3.5 ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {performance}
                                  </span>
                                </div>
                              </div>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      // Desktop Table View for Game Ratings
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Game Name</th>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Average Rating</th>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Custom Feedback</th>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium flex items-center gap-2">
                                Performance
                                <button 
                                  onClick={() => setShowPerformanceCriteria(!showPerformanceCriteria)}
                                  className="text-blue-500 hover:text-blue-700 text-sm"
                                  title="Rating Criteria"
                                >
                                  ℹ️
                                </button>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {gameRows.map((g,i)=>{
                              const gameName = allGames.find(game => game.gameId === g.gameId)?.name || g.gameId;
                              const rating = Number(g.rating);
                              const performance = rating >= 4.5 ? '🔥 Excellent' : rating >= 4.0 ? '👍 Good' : rating >= 3.5 ? '👌 Average' : '⚠️ Needs Improvement';
                              
                              return (
                                <tr key={i} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium text-gray-900">
                                    <button 
                                      onClick={() => loadGameDetails(g.gameId)}
                                      className="text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                      {gameName}
                                    </button>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-yellow-500 font-bold text-lg">{rating.toFixed(1)} ★</span>
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">{g.customFeedbackCount || 'N/A'}</td>
                                  <td className="px-4 py-3">
                                    <span className="text-sm">{performance}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">🎯</div>
                      <p className="text-gray-500">No game ratings available yet.</p>
                    </div>
                  )}
                  </div>

                  {/* Performance Criteria Modal */}
                  {showPerformanceCriteria && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50" onClick={() => setShowPerformanceCriteria(false)}>
                      <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-bold">📊 Rating Criteria</h3>
                          <button 
                            onClick={() => setShowPerformanceCriteria(false)}
                            className="text-gray-500 hover:text-gray-700 text-2xl"
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="border-l-4 border-orange-500 pl-4">
                            <h4 className="font-semibold text-orange-700">🔥 Excellent (4.5 - 5.0)</h4>
                            <p className="text-sm text-gray-600">Outstanding performance with high participant satisfaction</p>
                          </div>
                          
                          <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold text-green-700">👍 Good (4.0 - 4.4)</h4>
                            <p className="text-sm text-gray-600">Generally satisfactory performance</p>
                          </div>
                          
                          <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold text-yellow-700">👌 Average (3.5 - 3.9)</h4>
                            <p className="text-sm text-gray-600">Moderate satisfaction level</p>
                          </div>
                          
                          <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold text-red-700">⚠️ Needs Improvement (0.0 - 3.4)</h4>
                            <p className="text-sm text-gray-600">Participants were dissatisfied with the game</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Top 3 Activities Section */}
                {summary && summary.top3Activities && summary.top3Activities.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-4 flex items-center">
                      <span className="mr-2">📊</span>
                      Top 3 Activities Analysis
                    </h4>
                    
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Side - Activity Frequency Chart */}
                        <div>
                          <h5 className="font-medium text-blue-700 mb-4 flex items-center">
                            <span className="mr-2">📈</span>
                            Activity Frequency
                          </h5>
                          <div className="space-y-3">
                            {(() => {
                              // 모든 활동을 수집하고 빈도 계산
                              const allActivities = [];
                              summary.top3Activities.forEach(activities => {
                                if (activities && typeof activities === 'string') {
                                  const activityList = activities.split(',').map(a => a.trim()).filter(a => a);
                                  allActivities.push(...activityList);
                                }
                              });
                              
                              // 빈도 계산
                              const activityCount = {};
                              allActivities.forEach(activity => {
                                activityCount[activity] = (activityCount[activity] || 0) + 1;
                              });
                              
                              // 빈도순으로 정렬
                              const sortedActivities = Object.entries(activityCount)
                                .sort(([,a], [,b]) => b - a)
                                .slice(0, 10); // 상위 10개만 표시
                              
                              const maxCount = sortedActivities.length > 0 ? Math.max(...sortedActivities.map(([,count]) => count)) : 1;
                              
                              if (sortedActivities.length === 0) {
                                return (
                                  <div className="text-center py-8 text-gray-500">
                                    <div className="text-4xl mb-2">📊</div>
                                    <p>No activity data available yet.</p>
                                    <p className="text-sm mt-2">Members need to submit feedback with their top 3 activities.</p>
                                  </div>
                                );
                              }
                              
                              return sortedActivities.map(([activity, count]) => {
                                const percentage = (count / allActivities.length) * 100;
                                const barWidth = (count / maxCount) * 100;
                                
                                return (
                                  <div key={activity} className={`${isMobile ? 'flex flex-col space-y-2' : 'flex items-center space-x-3'}`}>
                                    <div className={`${isMobile ? 'w-full' : 'flex-1 min-w-0'}`}>
                                      <div className={`text-sm font-medium text-gray-900 ${isMobile ? 'break-words' : 'truncate'} flex items-center space-x-2`}>
                                        <span className="text-lg">{activity}</span>
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {count} times ({percentage.toFixed(1)}%)
                                      </div>
                                    </div>
                                    <div className={`${isMobile ? 'w-full' : 'flex-1'}`}>
                                      <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                          style={{ width: `${barWidth}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>

                        {/* Right Side - Top 3 List */}
                        <div>
                          <h5 className="font-medium text-purple-700 mb-4 flex items-center">
                            <span className="mr-2">🏆</span>
                            Top 3 Activities
                          </h5>
                          <div className="space-y-4">
                            {(() => {
                              // 모든 활동을 수집하고 빈도 계산
                              const allActivities = [];
                              summary.top3Activities.forEach(activities => {
                                if (activities && typeof activities === 'string') {
                                  const activityList = activities.split(',').map(a => a.trim()).filter(a => a);
                                  allActivities.push(...activityList);
                                }
                              });
                              
                              // 빈도 계산
                              const activityCount = {};
                              allActivities.forEach(activity => {
                                activityCount[activity] = (activityCount[activity] || 0) + 1;
                              });
                              
                              // 빈도순으로 정렬하고 상위 3개만 표시
                              const sortedActivities = Object.entries(activityCount)
                                .sort(([,a], [,b]) => b - a)
                                .slice(0, 3);
                              
                              return sortedActivities.map(([activity, count], index) => {
                                const percentage = (count / allActivities.length) * 100;
                                const rank = index + 1;
                                
                                return (
                                  <div key={activity} className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
                                    <div className={`${isMobile ? 'flex flex-col space-y-3' : 'flex items-center space-x-4'}`}>
                                      {/* Medal Only */}
                                      <div className={`${isMobile ? 'flex justify-center' : 'flex-shrink-0'}`}>
                                        <div className="w-16 h-16 flex items-center justify-center">
                                          <span className="text-5xl">
                                            {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Activity Info */}
                                      <div className={`${isMobile ? 'w-full text-center' : 'flex-1 min-w-0'}`}>
                                        <div className="mb-1">
                                          <h6 className={`text-lg font-bold text-gray-900 ${isMobile ? 'break-words' : 'truncate'}`}>
                                            {activity}
                                          </h6>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                          <span className="font-semibold text-purple-600">{count} times</span>
                                          <span className="mx-2">•</span>
                                          <span className="text-gray-500">{percentage.toFixed(1)}% of responses</span>
                                        </div>
                                      </div>
                                      
                                      {/* Decorative Icon */}
                                      <div className={`${isMobile ? 'flex justify-center' : 'flex-shrink-0'} text-3xl opacity-60`}>
                                        {rank === 1 ? '👑' : rank === 2 ? '⭐' : '✨'}
                                      </div>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      </div>
                      
                      {/* Outing Suggestions Section */}
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <h5 className="font-medium text-green-700 mb-4 flex items-center">
                          <span className="mr-2">🎯</span>
                          Outing Suggestions
                        </h5>
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {(() => {
                              // 모든 활동을 수집하고 빈도 계산
                              const allActivities = [];
                              summary.top3Activities.forEach(activities => {
                                if (activities && typeof activities === 'string') {
                                  const activityList = activities.split(',').map(a => a.trim()).filter(a => a);
                                  allActivities.push(...activityList);
                                }
                              });
                              
                              // 빈도 계산
                              const activityCount = {};
                              allActivities.forEach(activity => {
                                activityCount[activity] = (activityCount[activity] || 0) + 1;
                              });
                              
                              // 빈도순으로 정렬하고 상위 3개만 표시
                              const sortedActivities = Object.entries(activityCount)
                                .sort(([,a], [,b]) => b - a)
                                .slice(0, 3);
                              
                              // 활동별 추천 장소 및 팁 (Taipei, Taiwan 기반)
                              const getSuggestions = (activity, rank) => {
                                // 이모지가 포함된 활동명에서 이모지 제거
                                const cleanActivity = activity.replace(/^[^\w\s]+/, '').trim();
                                
                                const suggestions = {
                                  'Picnic in the Park': {
                                    places: ['Daan Forest Park', 'Xiangshan Hiking Trail', 'Riverside Park'],
                                    tips: ['Check weather forecast', 'Bring portable chairs', 'Prepare mosquito repellent'],
                                    emoji: '🧺'
                                  },
                                  'Karaoke Nights': {
                                    places: ['KT Happy Music Japanese Karaoke', 'Party World KTV', 'Cashbox KTV'],
                                    tips: ['Book in advance', 'Bring ID for verification', 'Prepare popular songs list'],
                                    emoji: '🎤'
                                  },
                                  'Board Game Nights': {
                                    places: ['Board Game Cafe', 'Cafe & Game Space', 'Member\'s home'],
                                    tips: ['Bring various games', 'Prepare snacks', 'Allocate enough time'],
                                    emoji: '🎲'
                                  },
                                  'EDM festival & Night club': {
                                    places: ['OMNI Nightclub', 'AI Nightclub', 'Wave Club'],
                                    tips: ['Dress code required', 'Bring valid ID', 'Check event schedule'],
                                    emoji: '🎵'
                                  },
                                  'Bouldering & Running crew': {
                                    places: ['B-Pump Climbing Gym', 'Taipei Riverside', 'Elephant Mountain'],
                                    tips: ['Wear proper gear', 'Bring water bottle', 'Warm up before activity'],
                                    emoji: '🧗'
                                  },
                                  'Going to the beach': {
                                    places: ['Fulong Beach', 'Baishawan Beach', 'Wanli Beach'],
                                    tips: ['Check weather conditions', 'Bring sunscreen', 'Prepare beach essentials'],
                                    emoji: '🏖️'
                                  },
                                  'Hiking & Nature walks': {
                                    places: ['Yangmingshan National Park', 'Elephant Mountain', 'Xiangshan Trail'],
                                    tips: ['Wear comfortable shoes', 'Bring water and snacks', 'Check trail conditions'],
                                    emoji: '🥾'
                                  },
                                  '2-day Taiwan traveling': {
                                    places: ['Yilan', 'Tamshui', 'Keelung'],
                                    tips: ['Book accommodation early', 'Plan transportation', 'Research local attractions'],
                                    emoji: '✈️'
                                  },
                                  'Drink at a bar': {
                                    places: ['Brass Monkey', 'Barcode', 'On tap'],
                                    tips: ['Check dress code', 'Bring valid ID', 'Set budget limit'],
                                    emoji: '🍻'
                                  },
                                  'Language Exchange meetups': {
                                    places: ['Cafe & Language Exchange', 'Community Centers', 'Online platforms'],
                                    tips: ['Prepare conversation topics', 'Bring language materials', 'Be open to learning'],
                                    emoji: '🗣️'
                                  },
                                  'Go Bowling': {
                                    places: ['Taipei Bowling Center', 'E7 play', 'AMF Bowling'],
                                    tips: ['Book lanes in advance', 'Wear comfortable clothes', 'Bring extra socks'],
                                    emoji: '🎳'
                                  },
                                  'BBQ night': {
                                    places: ['Riverside BBQ Area', 'Tamshui BBQ', 'BBQ Restaurant'],
                                    tips: ['Prepare ingredients', 'Check weather', 'Bring cooking utensils'],
                                    emoji: '🍖'
                                  }
                                };
                                
                                return suggestions[cleanActivity] || suggestions[activity] || {
                                  places: ['Various locations', 'Discuss with members', 'Online platforms'],
                                  tips: ['Plan in advance', 'Gather member opinions', 'Safety first'],
                                  emoji: '🎯'
                                };
                              };
                              
                              return sortedActivities.map(([activity, count], index) => {
                                const rank = index + 1;
                                // 이모지가 포함된 활동명에서 이모지 제거
                                const cleanActivity = activity.replace(/^[^\w\s]+/, '').trim();
                                const suggestion = getSuggestions(activity, rank);
                                
                                return (
                                  <div key={activity} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-center space-x-2 mb-3">
                                      <span className="text-2xl">{suggestion.emoji}</span>
                                      <h6 className="font-semibold text-gray-900">{cleanActivity}</h6>
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                        #{rank}
                                      </span>
                                    </div>
                                    
                                    <div className="space-y-3">
                                      <div>
                                        <h6 className="text-sm font-medium text-gray-700 mb-1">📍 Recommended Places</h6>
                                        <ul className="text-xs text-gray-600 space-y-1">
                                          {suggestion.places.map((place, idx) => (
                                            <li key={idx} className="flex items-center">
                                              <span className="w-1 h-1 bg-green-500 rounded-full mr-2"></span>
                                              {place}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      
                                      <div>
                                        <h6 className="text-sm font-medium text-gray-700 mb-1">💡 Preparation Tips</h6>
                                        <ul className="text-xs text-gray-600 space-y-1">
                                          {suggestion.tips.map((tip, idx) => (
                                            <li key={idx} className="flex items-center">
                                              <span className="w-1 h-1 bg-blue-500 rounded-full mr-2"></span>
                                              {tip}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                          
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center space-x-2">
                              <span className="text-blue-600">💡</span>
                              <p className="text-sm text-blue-800">
                                <strong>Tip:</strong> These recommendations are based on member preferences. 
                                Please discuss with members to choose the optimal location and time when planning actual events!
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Participant Comments Section */}
                {summary && (summary.goodPoints || summary.improvements || summary.comments) && (
                  <div className="mt-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="font-semibold mb-4 flex items-center">
                      <span className="mr-2">💬</span>
                      Participant Comments
                    </h4>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Good Points */}
                      {summary.goodPoints && summary.goodPoints.length > 0 && (
                        <div>
                          <h5 className="font-medium text-green-700 mb-3 flex items-center">
                            <span className="mr-2">👍</span>
                            What they liked most ({summary.goodPoints.length})
                          </h5>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {summary.goodPoints.map((comment, index) => (
                              <div key={index} className="bg-green-50 border-l-4 border-green-200 p-3 rounded-r-lg">
                                <p className="text-sm text-green-800 italic">"{comment}"</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Improvements */}
                      {summary.improvements && summary.improvements.length > 0 && (
                        <div>
                          <h5 className="font-medium text-orange-700 mb-3 flex items-center">
                            <span className="mr-2">💡</span>
                            Suggestions for improvement ({summary.improvements.length})
                          </h5>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {summary.improvements.map((comment, index) => (
                              <div key={index} className="bg-orange-50 border-l-4 border-orange-200 p-3 rounded-r-lg">
                                <p className="text-sm text-orange-800 italic">"{comment}"</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Legacy combined comments (if no separated data available) */}
                      {summary.comments && summary.comments.length > 0 && !summary.goodPoints && !summary.improvements && (
                        <div className="lg:col-span-2">
                          <h5 className="font-medium text-gray-700 mb-3 flex items-center">
                            <span className="mr-2">💭</span>
                            General Comments ({summary.comments.length})
                          </h5>
                          <div className="space-y-3 max-h-64 overflow-y-auto">
                            {summary.comments.slice(0, 10).map((comment, index) => (
                              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-sm text-gray-700 italic">"{comment}"</p>
                              </div>
                            ))}
                            {summary.comments.length > 10 && (
                              <div className="text-center text-sm text-gray-500">
                                ... and {summary.comments.length - 10} more comments
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
              </div>
            )}

                {/* Detailed Feedback Responses Section */}
                <div className="mt-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                  <span className="mr-2">📋</span>
                    Detailed Feedback Responses
                  </h4>
                  {feedbackDetails.length > 0 ? (
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                      {isMobile ? (
                        // Mobile Card View for Feedback Details
                        <div className="space-y-4">
                          {feedbackDetails.map((feedback, index) => (
                            <div key={feedback.id} className="bg-gray-50 p-4 rounded-lg border">
                              <div className="space-y-3">
                                <div className="flex justify-between items-start">
                                  <h5 className="font-semibold text-gray-900">Response #{index + 1}</h5>
                                  <span className="text-yellow-500 font-bold text-lg">{feedback.overall || 0} ★</span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Leadership Interest:</span>
                                    <div className="mt-1">
                                      {feedback.leadershipInterest && feedback.leadershipInterest.startsWith('yes') ? (
                                        <div className="text-green-600">
                                          <div className="font-medium">Yes!</div>
                                          {feedback.leadershipInterest.includes(':') && (
                                            <div className="text-xs text-gray-500">
                                              ID: {feedback.leadershipInterest.split(':')[1]}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">No</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Reels Participation:</span>
                                    <div className="mt-1">
                                      {feedback.reelsParticipation && feedback.reelsParticipation.startsWith('yes') ? (
                                        <div className="text-purple-600">
                                          <div className="font-medium">Yes!</div>
                                          {feedback.reelsParticipation.includes(':') && (
                                            <div className="text-xs text-gray-500">
                                              ID: {feedback.reelsParticipation.split(':')[1]}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">No</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="text-sm text-gray-500">
                                  Submitted: {new Date(feedback.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                          </div>
                          ))}
                        </div>
                      ) : (
                        // Desktop Table View for Feedback Details
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Response #</th>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Overall Rating</th>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Leadership Interest</th>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Reels Participation</th>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Submitted</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                              {feedbackDetails.map((feedback, index) => (
                                <tr key={feedback.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium text-gray-900">#{index + 1}</td>
                                  <td className="px-4 py-3">
                                    <span className="text-yellow-500 font-bold text-lg">{feedback.overall || 0} ★</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    {feedback.leadershipInterest && feedback.leadershipInterest.startsWith('yes') ? (
                                      <div className="text-green-600">
                                        <div className="font-medium">Yes!</div>
                                        {feedback.leadershipInterest.includes(':') && (
                                          <div className="text-xs text-gray-500">
                                            ID: {feedback.leadershipInterest.split(':')[1]}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">No</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {feedback.reelsParticipation && feedback.reelsParticipation.startsWith('yes') ? (
                                      <div className="text-purple-600">
                                        <div className="font-medium">Yes!</div>
                                        {feedback.reelsParticipation.includes(':') && (
                                          <div className="text-xs text-gray-500">
                                            ID: {feedback.reelsParticipation.split(':')[1]}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">No</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-gray-500">
                                    {new Date(feedback.createdAt).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                
                      <div className="mt-4 flex justify-end">
                  <button 
                    onClick={async () => await downloadFeedbackXLSX(feedbackDetails)}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-200 focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 font-medium shadow-sm"
                  >
                    📥 Download XLSX
                  </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">📋</div>
                      <p className="text-gray-500">No detailed feedback responses available yet.</p>
                    </div>
                  )}
                </div>
              </div>
            )}


          </div>
        )}

        {activeTab === 'past' && (
          <div>
            {/* 고정된 체크박스 영역 */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-4 mb-6 shadow-sm">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                {/* 필터 영역 */}
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <label htmlFor="branch-filter" className="block text-sm font-medium text-gray-700">Filter by Branch</label>
                    <select 
                      id="branch-filter"
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      className="mt-1 block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                      <option value="all">All Branches</option>
                      <option value="NCCU">NCCU</option>
                      <option value="NTU">NTU</option>
                      <option value="Taipei">Taipei</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700">Filter by Date</label>
                    <select 
                      id="date-filter"
                      value={selectedDateRange}
                      onChange={(e) => {
                        setSelectedDateRange(e.target.value);
                        if (e.target.value !== 'custom') {
                          setShowCustomDatePicker(false);
                        }
                      }}
                      className="mt-1 block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">1 Week</option>
                      <option value="month">1 Month</option>
                      <option value="3months">3 Months</option>
                      <option value="6months">6 Months</option>
                      <option value="year">1 Year</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                  {selectedDateRange === 'custom' && (
                    <div className="flex gap-2 items-end">
                      <div>
                        <label htmlFor="start-date-past" className="block text-sm font-medium text-gray-700">From</label>
                        <input
                          type="date"
                          id="start-date-past"
                          value={customDateRange.start}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        />
                      </div>
                      <div>
                        <label htmlFor="end-date-past" className="block text-sm font-medium text-gray-700">To</label>
                        <input
                          type="date"
                          id="end-date-past"
                          value={customDateRange.end}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* 선택 영역 */}
                <div className="flex items-center gap-4 bg-blue-50 px-4 py-2 rounded-lg">
                  <span className="text-sm text-gray-700 font-medium">{filteredEvents.length} events</span>
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={selectedPastEvents.size === filteredEvents.length && filteredEvents.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded"
                    />
                    Select All
                  </label>
                  {selectedPastEvents.size > 0 && (
                    <span className="text-sm text-blue-600 font-semibold">
                      {selectedPastEvents.size} selected
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* 이벤트 목록 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Past Event Feedback</h2>
              
              {selectedPastEvents.size === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">📊</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Events Selected</h3>
                  <p className="text-gray-500 mb-4">Please select one or more past events to view aggregated feedback data and game ratings.</p>
                  <div className="text-sm text-gray-400">
                    💡 Select events from the list above to analyze their feedback and game performance
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-blue-800">
                      📊 Aggregated Data ({selectedPastEvents.size} events selected)
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={downloadXLSX}
                        className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 flex items-center gap-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 font-medium shadow-sm"
                      >
                        📥 Download XLSX
                      </button>
                    <button
                      onClick={() => setSelectedPastEvents(new Set())}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear Selection
                    </button>
                    </div>
                  </div>
                  
                  {aggregatedData ? (
                    <div className="space-y-6">
                      {/* Enhanced Summary Cards */}
                      <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h4 className="font-semibold mb-4 flex items-center">
                          <span className="mr-2">📊</span>
                          Key Metrics
                        </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center mb-6">
                          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <div className="text-sm text-gray-600 font-medium">Total Submissions</div>
                            <div className="text-2xl font-bold text-blue-800">{aggregatedData.totalSubmissions}</div>
                      </div>
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <div className="text-sm text-gray-500">Overall Rating</div>
                          <div className="text-2xl font-bold text-yellow-500">{aggregatedData.avgOverall.toFixed(1)} ★</div>
                      </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <div className="text-sm text-gray-500">Response Rate</div>
                          <div className="text-2xl font-bold text-purple-600">
                            {aggregatedData.avgResponseRate ? `${aggregatedData.avgResponseRate.toFixed(1)}%` : 'N/A'}
                      </div>
                      </div>
                      </div>
                      
                      {/* Additional Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <div className="text-sm text-gray-500">Participants Fit</div>
                          <div className="text-xl font-bold text-gray-700">
                            {aggregatedData.avgParticipantsFit ? aggregatedData.avgParticipantsFit.toFixed(1) : 'N/A'} ★
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <div className="text-sm text-gray-500">Interaction Opportunity</div>
                          <div className="text-xl font-bold text-gray-700">
                            {aggregatedData.avgInteractionOpportunity ? aggregatedData.avgInteractionOpportunity.toFixed(1) : 'N/A'} ★
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Game Ratings Section */}
                      <div className="mt-6">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <span className="mr-2">🎮</span>
                          Game Ratings
                        </h4>
                        {aggregatedData.games && aggregatedData.games.length > 0 ? (
                          <div className="bg-white p-6 rounded-lg shadow-sm border">
                            {isMobile ? (
                              // Mobile Card View for Past Games
                              <div className="space-y-4">
                                {aggregatedData.games.map((game, i) => {
                                  const rating = Number(game.avgRating);
                                  const performance = rating >= 4.5 ? '🔥 Excellent' : rating >= 4.0 ? '👍 Good' : rating >= 3.5 ? '👌 Average' : '⚠️ Needs Improvement';
                                  
                                  return (
                                    <div key={i} className="bg-gray-50 p-4 rounded-lg border">
                                      <div className="space-y-3">
                                        <div className="flex justify-between items-start">
                                          <button 
                                            onClick={() => loadGameDetails(game.gameId)}
                                            className="text-blue-600 hover:text-blue-800 hover:underline font-semibold text-left"
                                          >
                                            {game.gameName || `Game ${i + 1}`}
                                          </button>
                                          <div className="flex items-center gap-2">
                                            <span className="text-lg font-bold text-blue-600">{rating.toFixed(1)}</span>
                                            <span className="text-sm text-gray-500">★</span>
                                          </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <span className="text-sm font-medium text-gray-600">Total Responses:</span>
                                            <div className="mt-1 text-sm text-gray-700">{game.totalResponses || 'N/A'}</div>
                                          </div>
                                          
                                          <div>
                                            <span className="text-sm font-medium text-gray-600">Performance:</span>
                                            <div className="mt-1">
                                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                rating >= 4.5 ? 'bg-emerald-100 text-emerald-700' :
                                                rating >= 3.5 ? 'bg-amber-100 text-amber-700' :
                                                'bg-red-100 text-red-700'
                                              }`}>
                                                {performance}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              // Desktop Table View for Past Games
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-gray-600 font-medium">Game Name</th>
                                      <th className="px-4 py-3 text-left text-gray-600 font-medium">Average Rating</th>
                                      <th className="px-4 py-3 text-left text-gray-600 font-medium">Total Responses</th>
                                      <th className="px-4 py-3 text-left text-gray-600 font-medium flex items-center gap-2">
                                        Performance
                                        <button 
                                          onClick={() => setShowPerformanceCriteria(!showPerformanceCriteria)}
                                          className="text-blue-500 hover:text-blue-700 text-sm"
                                          title="Rating Criteria"
                                        >
                                          ℹ️
                                        </button>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-100">
                                    {aggregatedData.games.map((game, i)=>{
                                      const rating = Number(game.avgRating);
                                      const performance = rating >= 4.5 ? '🔥 Excellent' : rating >= 4.0 ? '👍 Good' : rating >= 3.5 ? '👌 Average' : '⚠️ Needs Improvement';
                                      
                                      return (
                                        <tr key={i} className="hover:bg-gray-50">
                                          <td className="px-4 py-3 font-medium text-gray-900">
                                            <button 
                                              onClick={() => loadGameDetails(game.gameId)}
                                              className="text-blue-600 hover:text-blue-800 hover:underline"
                                            >
                                              {game.gameName || `Game ${i + 1}`}
                                            </button>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className="text-yellow-500 font-bold text-lg">{rating.toFixed(1)} ★</span>
                                          </td>
                                          <td className="px-4 py-3 text-gray-600">{game.totalResponses || 'N/A'}</td>
                                          <td className="px-4 py-3">
                                            <span className="text-sm">{performance}</span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <div className="text-4xl mb-2">🎯</div>
                            <p className="text-gray-500">No game ratings available yet.</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Top 3 Activities Analysis */}
                      {aggregatedData.top3Activities && aggregatedData.top3Activities.length > 0 && (
                        <div className="mt-6">
                          <h4 className="font-semibold mb-4 flex items-center">
                            <span className="mr-2">📊</span>
                            Top 3 Activities Analysis
                          </h4>
                          
                          <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                              {/* Left Side - Activity Frequency Chart */}
                        <div>
                                <h5 className="font-medium text-blue-700 mb-4 flex items-center">
                                  <span className="mr-2">📈</span>
                                  Activity Frequency
                                </h5>
                                <div className="space-y-3">
                                  {(() => {
                                    // 모든 활동을 수집하고 빈도 계산
                                    const allActivities = [];
                                    aggregatedData.top3Activities.forEach(activities => {
                                      if (activities && typeof activities === 'string') {
                                        const activityList = activities.split(',').map(a => a.trim()).filter(a => a);
                                        allActivities.push(...activityList);
                                      }
                                    });
                                    
                                    // 빈도 계산
                                    const activityCount = {};
                                    allActivities.forEach(activity => {
                                      activityCount[activity] = (activityCount[activity] || 0) + 1;
                                    });
                                    
                                    // 빈도순으로 정렬
                                    const sortedActivities = Object.entries(activityCount)
                                      .sort(([,a], [,b]) => b - a)
                                      .slice(0, 10); // 상위 10개만 표시
                                    
                                    const maxCount = sortedActivities.length > 0 ? Math.max(...sortedActivities.map(([,count]) => count)) : 1;
                                    
                                    return sortedActivities.map(([activity, count]) => {
                                      const percentage = (count / allActivities.length) * 100;
                                      const barWidth = (count / maxCount) * 100;
                                      
                                      return (
                                        <div key={activity} className={`${isMobile ? 'flex flex-col space-y-2' : 'flex items-center space-x-3'}`}>
                                          <div className={`${isMobile ? 'w-full' : 'flex-1 min-w-0'}`}>
                                            <div className={`text-sm font-medium text-gray-900 ${isMobile ? 'break-words' : 'truncate'}`}>
                                              {activity}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {count} times ({percentage.toFixed(1)}%)
                                            </div>
                                          </div>
                                          <div className={`${isMobile ? 'w-full' : 'flex-1'}`}>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                              <div 
                                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${barWidth}%` }}
                                              ></div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>

                              {/* Right Side - Top 3 List */}
                              <div>
                                <h5 className="font-medium text-purple-700 mb-4 flex items-center">
                                  <span className="mr-2">🏆</span>
                                  Top 3 Activities
                                </h5>
                                <div className="space-y-4">
                                  {(() => {
                                    // 모든 활동을 수집하고 빈도 계산
                                    const allActivities = [];
                                    aggregatedData.top3Activities.forEach(activities => {
                                      if (activities && typeof activities === 'string') {
                                        const activityList = activities.split(',').map(a => a.trim()).filter(a => a);
                                        allActivities.push(...activityList);
                                      }
                                    });
                                    
                                    // 빈도 계산
                                    const activityCount = {};
                                    allActivities.forEach(activity => {
                                      activityCount[activity] = (activityCount[activity] || 0) + 1;
                                    });
                                    
                                    // 빈도순으로 정렬하고 상위 3개만 표시
                                    const sortedActivities = Object.entries(activityCount)
                                      .sort(([,a], [,b]) => b - a)
                                      .slice(0, 3);
                                    
                                    return sortedActivities.map(([activity, count], index) => {
                                      const percentage = (count / allActivities.length) * 100;
                                      const rank = index + 1;
                                      
                                      return (
                                        <div key={activity} className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
                                          <div className={`${isMobile ? 'flex flex-col space-y-3' : 'flex items-center space-x-4'}`}>
                                            {/* Medal Only */}
                                            <div className={`${isMobile ? 'flex justify-center' : 'flex-shrink-0'}`}>
                                              <div className="w-16 h-16 flex items-center justify-center">
                                                <span className="text-5xl">
                                                  {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
                                                </span>
                                              </div>
                                            </div>
                                            
                                            {/* Activity Info */}
                                            <div className={`${isMobile ? 'w-full text-center' : 'flex-1 min-w-0'}`}>
                                              <div className="mb-1">
                                                <h6 className={`text-lg font-bold text-gray-900 ${isMobile ? 'break-words' : 'truncate'}`}>
                                                  {activity}
                                                </h6>
                                              </div>
                                              <div className="text-sm text-gray-600">
                                                <span className="font-semibold text-purple-600">{count} times</span>
                                                <span className="mx-2">•</span>
                                                <span className="text-gray-500">{percentage.toFixed(1)}% of responses</span>
                                              </div>
                                            </div>
                                            
                                            {/* Decorative Icon */}
                                            <div className={`${isMobile ? 'flex justify-center' : 'flex-shrink-0'} text-3xl opacity-60`}>
                                              {rank === 1 ? '👑' : rank === 2 ? '⭐' : '✨'}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                          </div>
                          </div>
                        </div>
                      )}

                      {/* Participant Comments Section */}
                      {aggregatedData.goodPoints || aggregatedData.improvements || aggregatedData.comments ? (
                      <div className="mt-6">
                          <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <h4 className="font-semibold mb-4 flex items-center">
                              <span className="mr-2">💬</span>
                              Participant Comments
                            </h4>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Good Points */}
                              {aggregatedData.goodPoints && aggregatedData.goodPoints.length > 0 && (
                                <div>
                                  <h5 className="font-medium text-green-700 mb-3 flex items-center">
                                    <span className="mr-2">👍</span>
                                    What they liked most ({aggregatedData.goodPoints.length})
                                  </h5>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {aggregatedData.goodPoints.map((comment, index) => (
                                      <div key={index} className="bg-green-50 border-l-4 border-green-200 p-3 rounded-r-lg">
                                        <p className="text-sm text-green-800 italic">"{comment}"</p>
                            </div>
                                    ))}
                          </div>
                          </div>
                        )}

                              {/* Improvements */}
                              {aggregatedData.improvements && aggregatedData.improvements.length > 0 && (
                                <div>
                                  <h5 className="font-medium text-orange-700 mb-3 flex items-center">
                                    <span className="mr-2">💡</span>
                                    Suggestions for improvement ({aggregatedData.improvements.length})
                                  </h5>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {aggregatedData.improvements.map((comment, index) => (
                                      <div key={index} className="bg-orange-50 border-l-4 border-orange-200 p-3 rounded-r-lg">
                                        <p className="text-sm text-orange-800 italic">"{comment}"</p>
                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Legacy combined comments (if no separated data available) */}
                              {aggregatedData.comments && aggregatedData.comments.length > 0 && !aggregatedData.goodPoints && !aggregatedData.improvements && (
                                <div className="lg:col-span-2">
                                  <h5 className="font-medium text-gray-700 mb-3 flex items-center">
                                    <span className="mr-2">💭</span>
                                    General Comments ({aggregatedData.comments.length})
                                  </h5>
                                  <div className="space-y-3 max-h-64 overflow-y-auto">
                                    {aggregatedData.comments.slice(0, 10).map((comment, index) => (
                                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-sm text-gray-700 italic">"{comment}"</p>
                                      </div>
                                    ))}
                                    {aggregatedData.comments.length > 10 && (
                                      <div className="text-center text-sm text-gray-500">
                                        ... and {aggregatedData.comments.length - 10} more comments
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {/* Detailed Feedback Responses */}
                      <div className="mt-6">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <span className="mr-2">📋</span>
                          Detailed Feedback Responses
                        </h4>
                        {aggregatedData.feedbackDetails && aggregatedData.feedbackDetails.length > 0 ? (
                          <div className="bg-white p-6 rounded-lg shadow-sm border">
                            {isMobile ? (
                              <div className="space-y-4">
                                <div className="text-center py-8">
                                  <div className="text-4xl mb-2">📱</div>
                                  <p className="text-gray-500">Mobile view coming soon</p>
                                </div>
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-gray-600 font-medium">Response #</th>
                                    <th className="px-4 py-3 text-left text-gray-600 font-medium">Overall Rating</th>
                                    <th className="px-4 py-3 text-left text-gray-600 font-medium">Leadership Interest</th>
                                    <th className="px-4 py-3 text-left text-gray-600 font-medium">Reels Participation</th>
                                    <th className="px-4 py-3 text-left text-gray-600 font-medium">Submitted</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                  {aggregatedData.feedbackDetails.map((feedback, index) => (
                                    <tr key={feedback.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 font-medium text-gray-900">#{index + 1}</td>
                                      <td className="px-4 py-3">
                                        <span className="text-yellow-500 font-bold text-lg">{feedback.overall || 0} ★</span>
                                      </td>
                                      <td className="px-4 py-3">
                                        {feedback.leadershipInterest && feedback.leadershipInterest.startsWith('yes') ? (
                                          <div className="text-green-600">
                                            <div className="font-medium">Yes!</div>
                                            {feedback.leadershipInterest.includes(':') && (
                                              <div className="text-xs text-gray-500">
                                                ID: {feedback.leadershipInterest.split(':')[1]}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">No</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3">
                                        {feedback.reelsParticipation && feedback.reelsParticipation.startsWith('yes') ? (
                                          <div className="text-purple-600">
                                            <div className="font-medium">Yes!</div>
                                            {feedback.reelsParticipation.includes(':') && (
                                              <div className="text-xs text-gray-500">
                                                ID: {feedback.reelsParticipation.split(':')[1]}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">No</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-gray-500 text-sm">
                                        {feedback.submittedAt ? new Date(feedback.submittedAt).toLocaleDateString() : 'N/A'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <div className="text-4xl mb-2">📋</div>
                            <p className="text-gray-500">No detailed feedback responses available yet.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500">Loading aggregated data...</div>
                  )}
                </div>
              )}

              <ul className="space-y-4">
                {filteredEvents.map(event => (
                  <li key={event.eventId || event.id} className="p-4 border rounded-lg sm:flex justify-between items-center space-y-4 sm:space-y-0 hover:bg-gray-50">
                    <div className="flex items-center gap-3 flex-grow">
                      <input
                        type="checkbox"
                        checked={selectedPastEvents.has(event.id || event.eventId)}
                        onChange={(e) => handleEventSelection(event.id || event.eventId, e.target.checked)}
                        className="rounded"
                      />
                      <div className="flex-grow">
                        <p className="font-bold text-lg text-gray-900">{event.eventTitle || event.title}</p>
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full mr-2">{event.branch}</span>
                          {new Date(event.eventDateTime || event.date).toLocaleDateString()}
                          {event.theme && (
                            <span className="ml-2 text-purple-600 bg-purple-100 px-2 py-1 rounded-full text-xs">{event.theme}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className={`flex gap-2 flex-shrink-0 ${isMobile ? 'flex-col' : 'flex-row'}`}>
                      <button 
                        onClick={() => {
                          const eventId = event.id || event.eventId;
                          if (selectedPastEventId === eventId) {
                            // 이미 선택된 이벤트를 다시 클릭하면 닫기
                            setSelectedPastEventId('');
                            setSelectedEventId('');
                          } else {
                            // 새로운 이벤트 선택
                            setSelectedPastEventId(eventId);
                            setSelectedEventId(eventId); // 기존 로직 유지
                            loadFeedbackDetails(eventId);
                          }
                        }} 
                        className={`text-sm bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors duration-200 font-medium ${isMobile ? 'w-full' : ''}`}
                      >
                        📊 View Feedback
                      </button>
                      {(userRole?.toLowerCase() === 'leader' || userRole?.toLowerCase() === 'president' || userRole?.toLowerCase() === 'admin') && (
                        <button 
                          onClick={() => handleRestoreToCurrent(event.id || event.eventId)} 
                          className={`text-sm bg-emerald-100 text-emerald-700 px-3 py-2 rounded-lg hover:bg-emerald-200 transition-colors duration-200 font-medium ${isMobile ? 'w-full' : ''}`}
                        >
                          🔄 Restore to Current
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {selectedPastEventId && (
              <div className="bg-white p-6 rounded-lg shadow-md mt-6">
                <h3 className="text-xl font-semibold mb-4">Feedback Summary (Event #{selectedPastEventId})</h3>
                {summary ? (
                  <div>
                    <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
                      <h4 className="font-semibold mb-4 flex items-center">
                        <span className="mr-2">📊</span>
                        Key Metrics
                      </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center mb-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Submissions</div>
                        <div className="text-2xl font-bold text-blue-600">{summary.count}</div>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Overall Avg</div>
                        <div className="text-2xl font-bold text-yellow-500">{Number(summary.overallAvg).toFixed(1)} ★</div>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Response Rate</div>
                        <div className="text-2xl font-bold text-purple-600">
                          {summary.responseRate !== undefined ? `${summary.responseRate.toFixed(1)}%` : 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Additional Metrics */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <div className="text-sm text-gray-500">Participants Fit</div>
                          <div className="text-xl font-bold text-gray-700">
                            {summary.participantsFitAvg ? Number(summary.participantsFitAvg).toFixed(1) : 'N/A'} ★
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <div className="text-sm text-gray-500">Interaction Opportunity</div>
                          <div className="text-xl font-bold text-gray-700">
                            {summary.interactionOpportunityAvg ? Number(summary.interactionOpportunityAvg).toFixed(1) : 'N/A'} ★
                            </div>
                          </div>
                        </div>
                    </div>

                  </div>
                ) : (
                  <p className="text-gray-500">No feedback data available.</p>
                )}
                {/* Game Ratings Section */}
                <div className="mt-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="font-semibold mb-3 flex items-center">
                      <span className="mr-2">🎮</span>
                      Game Ratings
                    </h4>
                    {gameRows.length > 0 ? (
                      isMobile ? (
                        // Mobile Card View for Staff Game Ratings
                        <div className="space-y-4">
                          {gameRows.map((g,i) => {
                            const gameName = allGames.find(game => game.gameId === g.gameId)?.name || g.gameId;
                            const rating = Number(g.rating);
                            const performance = rating >= 4.5 ? '🔥 Excellent' : rating >= 4.0 ? '👍 Good' : rating >= 3.5 ? '👌 Average' : '⚠️ Needs Improvement';
                            
                            return (
                              <div key={i} className="bg-gray-50 p-4 rounded-lg border">
                                <div className="space-y-3">
                                  <div className="flex justify-between items-start">
                                    <button 
                                      onClick={() => loadGameDetails(g.gameId)}
                                      className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                                    >
                                      {gameName}
                                    </button>
                                    <span className="text-yellow-500 font-bold text-lg">{rating.toFixed(1)} ★</span>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">Custom Feedback:</span>
                                      <div className="mt-1 text-sm text-gray-700">{g.customFeedbackCount || 'N/A'}</div>
                                    </div>
                                    
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">Performance:</span>
                                      <div className="mt-1">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                          rating >= 4.5 ? 'bg-emerald-100 text-emerald-700' :
                                          rating >= 3.5 ? 'bg-amber-100 text-amber-700' :
                                          'bg-red-100 text-red-700'
                                        }`}>
                                          {performance}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        // Desktop Table View for Staff Game Ratings
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Game Name</th>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Average Rating</th>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium">Custom Feedback</th>
                                <th className="px-4 py-3 text-left text-gray-600 font-medium flex items-center gap-2">
                                  Performance
                                  <button 
                                    onClick={() => setShowPerformanceCriteria(!showPerformanceCriteria)}
                                    className="text-blue-500 hover:text-blue-700 text-sm"
                                    title="Rating Criteria"
                                  >
                                    ℹ️
                                  </button>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                              {gameRows.map((g,i)=>{
                                const gameName = allGames.find(game => game.gameId === g.gameId)?.name || g.gameId;
                                const rating = Number(g.rating);
                                const performance = rating >= 4.5 ? '🔥 Excellent' : rating >= 4.0 ? '👍 Good' : rating >= 3.5 ? '👌 Average' : '⚠️ Needs Improvement';
                                
                                return (
                                  <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-900">
                                      <button 
                                        onClick={() => loadGameDetails(g.gameId)}
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                      >
                                        {gameName}
                                      </button>
                                  </td>
                                    <td className="px-4 py-3">
                                      <span className="text-yellow-500 font-bold text-lg">{rating.toFixed(1)} ★</span>
                                  </td>
                                    <td className="px-4 py-3 text-gray-600">{g.customFeedbackCount || 'N/A'}</td>
                                    <td className="px-4 py-3">
                                      <span className="text-sm">{performance}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-2">🎯</div>
                        <p className="text-gray-500">No game ratings available yet.</p>
                      </div>
                    )}
                  </div>

                  {/* Performance Criteria Modal */}
                  {showPerformanceCriteria && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50" onClick={() => setShowPerformanceCriteria(false)}>
                      <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-bold">📊 Rating Criteria</h3>
                          <button 
                            onClick={() => setShowPerformanceCriteria(false)}
                            className="text-gray-500 hover:text-gray-700 text-2xl"
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="border-l-4 border-orange-500 pl-4">
                            <h4 className="font-semibold text-orange-700">🔥 Excellent (4.5 - 5.0)</h4>
                            <p className="text-sm text-gray-600">Outstanding performance with high participant satisfaction</p>
                          </div>
                          
                          <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold text-green-700">👍 Good (4.0 - 4.4)</h4>
                            <p className="text-sm text-gray-600">Generally satisfactory performance</p>
                          </div>
                          
                          <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold text-yellow-700">👌 Average (3.5 - 3.9)</h4>
                            <p className="text-sm text-gray-600">Moderate satisfaction level</p>
                          </div>
                          
                          <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold text-red-700">⚠️ Needs Improvement (0.0 - 3.4)</h4>
                            <p className="text-sm text-gray-600">Participants were dissatisfied with the game</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                    </div>

                    {/* Top 3 Activities Section for Past Events */}
                    {summary && summary.top3Activities && summary.top3Activities.length > 0 && (
                  <div className="mt-6">
                        <h4 className="font-semibold mb-4 flex items-center">
                          <span className="mr-2">📊</span>
                          Top 3 Activities Analysis
                        </h4>
                        
                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Left Side - Activity Frequency Chart */}
                            <div>
                              <h5 className="font-medium text-blue-700 mb-4 flex items-center">
                                <span className="mr-2">📈</span>
                                Activity Frequency
                              </h5>
                              <div className="space-y-3">
                                {(() => {
                                  // 모든 활동을 수집하고 빈도 계산
                                  const allActivities = [];
                                  summary.top3Activities.forEach(activities => {
                                    if (activities && typeof activities === 'string') {
                                      const activityList = activities.split(',').map(a => a.trim()).filter(a => a);
                                      allActivities.push(...activityList);
                                    }
                                  });
                                  
                                  // 빈도 계산
                                  const activityCount = {};
                                  allActivities.forEach(activity => {
                                    activityCount[activity] = (activityCount[activity] || 0) + 1;
                                  });
                                  
                                  // 빈도순으로 정렬
                                  const sortedActivities = Object.entries(activityCount)
                                    .sort(([,a], [,b]) => b - a)
                                    .slice(0, 10); // 상위 10개만 표시
                                  
                                  const maxCount = Math.max(...sortedActivities.map(([,count]) => count));
                                  
                                  return sortedActivities.map(([activity, count]) => {
                                    const percentage = (count / allActivities.length) * 100;
                                    const barWidth = (count / maxCount) * 100;
                                    
                                    return (
                                      <div key={activity} className={`${isMobile ? 'flex flex-col space-y-2' : 'flex items-center space-x-3'}`}>
                                        <div className={`${isMobile ? 'w-full' : 'flex-1 min-w-0'}`}>
                                          <div className={`text-sm font-medium text-gray-900 ${isMobile ? 'break-words' : 'truncate'}`}>
                                            {activity}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {count} times ({percentage.toFixed(1)}%)
                                          </div>
                                        </div>
                                        <div className={`${isMobile ? 'w-full' : 'flex-1'}`}>
                                          <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                              style={{ width: `${barWidth}%` }}
                                            ></div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                          </div>

                            {/* Right Side - Top 3 List */}
                            <div>
                              <h5 className="font-medium text-purple-700 mb-4 flex items-center">
                                <span className="mr-2">🏆</span>
                                Top 3 Activities
                              </h5>
                              <div className="space-y-4">
                                {(() => {
                                  // 모든 활동을 수집하고 빈도 계산
                                  const allActivities = [];
                                  summary.top3Activities.forEach(activities => {
                                    if (activities && typeof activities === 'string') {
                                      const activityList = activities.split(',').map(a => a.trim()).filter(a => a);
                                      allActivities.push(...activityList);
                                    }
                                  });
                                  
                                  // 빈도 계산
                                  const activityCount = {};
                                  allActivities.forEach(activity => {
                                    activityCount[activity] = (activityCount[activity] || 0) + 1;
                                  });
                                  
                                  // 빈도순으로 정렬하고 상위 3개만 표시
                                  const sortedActivities = Object.entries(activityCount)
                                    .sort(([,a], [,b]) => b - a)
                                    .slice(0, 3);
                                  
                                  return sortedActivities.map(([activity, count], index) => {
                                    const percentage = (count / allActivities.length) * 100;
                                    const rank = index + 1;
                                    
                                    return (
                                      <div key={activity} className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
                                        <div className={`${isMobile ? 'flex flex-col space-y-3' : 'flex items-center space-x-4'}`}>
                                          {/* Medal Only */}
                                          <div className={`${isMobile ? 'flex justify-center' : 'flex-shrink-0'}`}>
                                            <div className="w-16 h-16 flex items-center justify-center">
                                              <span className="text-5xl">
                                                {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
                                              </span>
                                            </div>
                                          </div>
                                          
                                          {/* Activity Info */}
                                          <div className={`${isMobile ? 'w-full text-center' : 'flex-1 min-w-0'}`}>
                                            <div className="mb-1">
                                              <h6 className={`text-lg font-bold text-gray-900 ${isMobile ? 'break-words' : 'truncate'}`}>
                                                {activity}
                                              </h6>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                              <span className="font-semibold text-purple-600">{count} times</span>
                                              <span className="mx-2">•</span>
                                              <span className="text-gray-500">{percentage.toFixed(1)}% of responses</span>
                                            </div>
                                          </div>
                                          
                                          {/* Decorative Icon */}
                                          <div className={`${isMobile ? 'flex justify-center' : 'flex-shrink-0'} text-3xl opacity-60`}>
                                            {rank === 1 ? '👑' : rank === 2 ? '⭐' : '✨'}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                          </div>
                          </div>
                        </div>
                      </div>
                    )}

                {/* Participant Comments Section */}
                {summary && (summary.goodPoints || summary.improvements || summary.comments) && (
                <div className="mt-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                      <h4 className="font-semibold mb-4 flex items-center">
                        <span className="mr-2">💬</span>
                        Participant Comments
                  </h4>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Good Points */}
                        {summary.goodPoints && summary.goodPoints.length > 0 && (
                          <div>
                            <h5 className="font-medium text-green-700 mb-3 flex items-center">
                              <span className="mr-2">👍</span>
                              What they liked most ({summary.goodPoints.length})
                            </h5>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {summary.goodPoints.map((comment, index) => (
                                <div key={index} className="bg-green-50 border-l-4 border-green-200 p-3 rounded-r-lg">
                                  <p className="text-sm text-green-800 italic">"{comment}"</p>
                    </div>
                              ))}
                            </div>
                    </div>
                  )}

                        {/* Improvements */}
                        {summary.improvements && summary.improvements.length > 0 && (
                          <div>
                            <h5 className="font-medium text-orange-700 mb-3 flex items-center">
                              <span className="mr-2">💡</span>
                              Suggestions for improvement ({summary.improvements.length})
                            </h5>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {summary.improvements.map((comment, index) => (
                                <div key={index} className="bg-orange-50 border-l-4 border-orange-200 p-3 rounded-r-lg">
                                  <p className="text-sm text-orange-800 italic">"{comment}"</p>
                        </div>
                              ))}
                          </div>
                          </div>
                        )}

                        {/* Legacy combined comments (if no separated data available) */}
                        {summary.comments && summary.comments.length > 0 && !summary.goodPoints && !summary.improvements && (
                          <div className="lg:col-span-2">
                            <h5 className="font-medium text-gray-700 mb-3 flex items-center">
                              <span className="mr-2">💭</span>
                              General Comments ({summary.comments.length})
                            </h5>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                              {summary.comments.slice(0, 10).map((comment, index) => (
                                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                  <p className="text-sm text-gray-700 italic">"{comment}"</p>
                          </div>
                              ))}
                              {summary.comments.length > 10 && (
                                <div className="text-center text-sm text-gray-500">
                                  ... and {summary.comments.length - 10} more comments
                          </div>
                              )}
                      </div>
                    </div>
                  )}
                </div>
                    </div>
                  </div>
                )}

                {/* Detailed Feedback Responses Section */}
                <div className="mt-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <span className="mr-2">📋</span>
                    Detailed Feedback Responses
                  </h4>
                  {feedbackDetails.length > 0 ? (
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Response #</th>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Overall Rating</th>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Leadership Interest</th>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Reels Participation</th>
                              <th className="px-4 py-3 text-left text-gray-600 font-medium">Submitted</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {feedbackDetails.map((feedback, index) => (
                              <tr key={feedback.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-900">#{index + 1}</td>
                                <td className="px-4 py-3">
                                  <span className="text-yellow-500 font-bold text-lg">{feedback.overall || 0} ★</span>
                                </td>
                                <td className="px-4 py-3">
                                  {feedback.leadershipInterest && feedback.leadershipInterest.startsWith('yes') ? (
                                    <div className="text-green-600">
                                      <div className="font-medium">Yes!</div>
                                      {feedback.leadershipInterest.includes(':') && (
                                        <div className="text-xs text-gray-500">
                                          ID: {feedback.leadershipInterest.split(':')[1]}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">No</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {feedback.reelsParticipation && feedback.reelsParticipation.startsWith('yes') ? (
                                    <div className="text-purple-600">
                                      <div className="font-medium">Yes!</div>
                                      {feedback.reelsParticipation.includes(':') && (
                                        <div className="text-xs text-gray-500">
                                          ID: {feedback.reelsParticipation.split(':')[1]}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">No</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-sm">
                                  {feedback.submittedAt ? new Date(feedback.submittedAt).toLocaleDateString() : 'N/A'}
                                </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">📋</div>
                      <p className="text-gray-500">No detailed feedback responses available yet.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'staff' && (
          <div>
            <div className="mb-6 flex gap-4 flex-row">
              <div>
              <label htmlFor="branch-filter" className="block text-sm font-medium text-gray-700">Filter by Branch</label>
              <select 
                id="branch-filter"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="mt-1 block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Branches</option>
                <option value="NCCU">NCCU</option>
                <option value="NTU">NTU</option>
                <option value="Taipei">Taipei</option>
              </select>
              </div>
              <div>
                <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700">Filter by Date</label>
                <select 
                  id="date-filter"
                  value={selectedDateRange}
                  onChange={(e) => {
                    setSelectedDateRange(e.target.value);
                    if (e.target.value !== 'custom') {
                      setShowCustomDatePicker(false);
                    }
                  }}
                  className="mt-1 block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">1 Week</option>
                  <option value="month">1 Month</option>
                  <option value="3months">3 Months</option>
                  <option value="6months">6 Months</option>
                  <option value="year">1 Year</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              {selectedDateRange === 'custom' && (
                <div className="flex gap-2 items-end">
                  <div>
                    <label htmlFor="start-date-staff" className="block text-sm font-medium text-gray-700">From</label>
                    <input
                      type="date"
                      id="start-date-staff"
                      value={customDateRange.start}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    />
                  </div>
                  <div>
                    <label htmlFor="end-date-staff" className="block text-sm font-medium text-gray-700">To</label>
                    <input
                      type="date"
                      id="end-date-staff"
                      value={customDateRange.end}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold">Staff Performance Feedback</h2>
              </div>
              
              <ul className="space-y-4">
                {allEvents.filter(event => selectedBranch === 'all' || event.branch === selectedBranch).map(event => (
                  <li key={event.eventId || event.id} className="p-4 border rounded-lg hover:bg-gray-50">
                    <div className="space-y-3">
                      {/* 제목 */}
                      <h3 className="font-bold text-lg text-gray-900">{event.eventTitle || event.title}</h3>
                      
                      {/* 태그와 날짜 */}
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full text-sm w-fit">
                          {event.branch}
                        </span>
                        {movedToPast.has(event.id || event.eventId) && (
                          <span className="font-semibold text-orange-700 bg-orange-100 px-2 py-1 rounded-full text-sm w-fit">
                            📁 Archived
                          </span>
                        )}
                        <span className="text-sm text-gray-600">
                          {new Date(event.eventDateTime || event.date).toLocaleDateString()}
                        </span>
                      </div>
                      
                      {/* 버튼 */}
                      <div className="flex justify-end">
                        <button 
                          onClick={() => {
                            const eventId = event.id || event.eventId;
                            if (selectedEventId === eventId) {
                              // 이미 선택된 이벤트를 다시 클릭하면 닫기
                              setSelectedEventId('');
                            } else {
                              // 새로운 이벤트 선택
                            setSelectedEventId(eventId);
                            loadStaffFeedback(eventId);
                            }
                          }}
                          className="text-sm bg-purple-100 text-purple-800 px-3 py-2 rounded-lg hover:bg-purple-200"
                        >
                          👥 View Staff Feedback
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Staff Feedback Details */}
            {selectedEventId && staffFeedback && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-xl shadow-lg mt-6 border border-indigo-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-indigo-800 flex items-center">
                    <span className="mr-3 text-3xl">👥</span>
                    Staff Feedback Summary
                </h3>
                  <div className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-sm font-semibold">
                    Event #{selectedEventId}
                  </div>
                </div>
                
                {/* 통계 카드 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Self Evaluations</p>
                        <p className="text-3xl font-bold">{staffFeedback.self?.length || 0}</p>
                          </div>
                      <div className="text-4xl opacity-80">🔍</div>
                            </div>
                            </div>
                  
                  <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-xl text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Peer Evaluations</p>
                        <p className="text-3xl font-bold">{staffFeedback.peer?.length || 0}</p>
                            </div>
                      <div className="text-4xl opacity-80">👥</div>
                            </div>
                            </div>
                  
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 rounded-xl text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-100 text-sm">Most Helpful</p>
                        <p className="text-3xl font-bold">{staffFeedback.mostHelpful?.length || 0}</p>
                          </div>
                      <div className="text-4xl opacity-80">🏆</div>
                        </div>
                    </div>
                  </div>

                <div className="space-y-8">
                  {/* Self Evaluations */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-100 shadow-sm">
                    <h4 className="font-bold text-blue-800 mb-6 flex items-center text-lg">
                      <span className="mr-3 text-2xl">🔍</span>
                      Self Evaluations
                      <span className="ml-3 bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {staffFeedback.self?.length || 0}
                            </span>
                    </h4>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {staffFeedback.self && staffFeedback.self.length > 0 ? (
                        staffFeedback.self.map((feedback, index) => (
                          <div key={index} className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                                  {feedback.staffName.charAt(0).toUpperCase()}
                          </div>
                                <div>
                                  <span className="font-bold text-gray-900 text-lg">{feedback.staffName}</span>
                                  <div className="text-sm text-gray-500">{feedback.team}</div>
                                  <div className="text-sm text-blue-600 font-medium">
                                    Role: {feedback.role || 'Not specified'}
                            </div>
                            </div>
                            </div>
                          </div>
                            
                            {/* 평균 점수 표시 */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                              <div className="text-center bg-blue-50 p-3 rounded-lg flex flex-col h-full">
                                <div className="text-2xl mb-1">🚀</div>
                                <div className="text-gray-600 text-sm font-medium mb-2">Proactive</div>
                                <div className="font-bold text-blue-600 text-xl mb-2">{feedback.proactiveness}/5</div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-auto">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                    style={{width: `${(feedback.proactiveness / 5) * 100}%`}}
                                  ></div>
                        </div>
                    </div>
                              <div className="text-center bg-blue-50 p-3 rounded-lg flex flex-col h-full">
                                <div className="text-2xl mb-1">✅</div>
                                <div className="text-gray-600 text-sm font-medium mb-2">
                                  <span className="md:hidden">Task</span>
                                  <span className="hidden md:inline">Task Completion</span>
                  </div>
                                <div className="font-bold text-blue-600 text-xl mb-2">{feedback.taskCompletion}/5</div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-auto">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                    style={{width: `${(feedback.taskCompletion / 5) * 100}%`}}
                                  ></div>
                </div>
              </div>
                              <div className="text-center bg-blue-50 p-3 rounded-lg flex flex-col h-full">
                                <div className="text-2xl mb-1">💬</div>
                                <div className="text-gray-600 text-sm font-medium mb-2">
                                  <span className="md:hidden">Comm</span>
                                  <span className="hidden md:inline">Communication</span>
                                </div>
                                <div className="font-bold text-blue-600 text-xl mb-2">{feedback.communication}/5</div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-auto">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                    style={{width: `${(feedback.communication / 5) * 100}%`}}
                                  ></div>
                                </div>
                              </div>
                              <div className="text-center bg-blue-50 p-3 rounded-lg flex flex-col h-full">
                                <div className="text-2xl mb-1">⏰</div>
                                <div className="text-gray-600 text-sm font-medium mb-2">
                                  <span className="md:hidden">Time</span>
                                  <span className="hidden md:inline">Time Management</span>
                                </div>
                                <div className="font-bold text-blue-600 text-xl mb-2">{feedback.timeManagement}/5</div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-auto">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                    style={{width: `${(feedback.timeManagement / 5) * 100}%`}}
                                  ></div>
                                </div>
                              </div>
                          </div>
                            
                            {/* Self Reflection Questions */}
                            <div className="space-y-3">
                              <div>
                                <div className="text-sm font-medium text-blue-700 mb-2">💭 Self Reflection:</div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                                    <span className="font-medium">1. The Challenge:</span> "{feedback.teamChallenge || 'No response'}"
                                  </div>
                                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                                    <span className="font-medium">2. The Lesson:</span> "{feedback.proudOf || 'No response'}"
                                  </div>
                                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                                    <span className="font-medium">3. The Improvement:</span> "{feedback.improvements || 'No response'}"
                                  </div>
                                </div>
                              </div>
                          </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          <div className="text-4xl mb-4">🔍</div>
                          <p className="text-lg">No self evaluations submitted yet</p>
                          <p className="text-sm text-gray-400 mt-2">Self evaluations will appear here once submitted</p>
          </div>
        )}
                    </div>
                  </div>

                  {/* Peer Evaluations - Leader Only */}
                  {(userRole?.toLowerCase() === 'leader' || userRole?.toLowerCase() === 'admin') ? (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100 shadow-sm">
                      <h4 className="font-bold text-green-800 mb-6 flex items-center text-lg">
                        <span className="mr-3 text-2xl">👥</span>
                        Peer Evaluations
                        <span className="ml-3 bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                          {staffFeedback.peer?.length || 0}
                        </span>
                      </h4>
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {staffFeedback.peer && staffFeedback.peer.length > 0 ? (
                          staffFeedback.peer.map((staff, index) => (
                          <div key={index} className="bg-white p-4 rounded-lg border border-green-100 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                                  {staff.staffName.charAt(0).toUpperCase()}
                                </div>
          <div>
                                  <span className="font-bold text-gray-900 text-lg">{staff.staffName}</span>
                                  <div className="text-sm text-gray-500">
                                    {staff.evaluationCount} evaluation{staff.evaluationCount !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
            </div>

                            {/* 평균 점수 표시 */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div className="text-center bg-green-50 p-3 rounded-lg">
                                <div className="text-2xl mb-1">🎯</div>
                                <div className="text-gray-600 text-sm font-medium">Contribution</div>
                                <div className="font-bold text-green-600 text-xl">{staff.avgContribution}/5</div>
                                {/* 점수 바 */}
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                  <div 
                                    className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                                    style={{width: `${(staff.avgContribution / 5) * 100}%`}}
                                  ></div>
                      </div>
                      </div>
                              <div className="text-center bg-green-50 p-3 rounded-lg">
                                <div className="text-2xl mb-1">🤝</div>
                                <div className="text-gray-600 text-sm font-medium">Teamwork</div>
                                <div className="font-bold text-green-600 text-xl">{staff.avgTeamwork}/5</div>
                                {/* 점수 바 */}
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                  <div 
                                    className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                                    style={{width: `${(staff.avgTeamwork / 5) * 100}%`}}
                                  ></div>
                    </div>
                              </div>
                              <div className="text-center bg-green-50 p-3 rounded-lg">
                                <div className="text-2xl mb-1">💬</div>
                                <div className="text-gray-600 text-sm font-medium">Communication</div>
                                <div className="font-bold text-green-600 text-xl">{staff.avgCommunication}/5</div>
                                {/* 점수 바 */}
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                  <div 
                                    className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                                    style={{width: `${(staff.avgCommunication / 5) * 100}%`}}
                                  ></div>
                                </div>
                              </div>
                          </div>
                            
                            {/* 피드백 텍스트 */}
                            <div className="space-y-3">
                              {staff.allStrengths.length > 0 && (
                                <div>
                                  <div className="text-sm font-medium text-green-700 mb-2">💪 Strengths:</div>
                                  <div className="space-y-1">
                                    {staff.allStrengths.map((strength, idx) => (
                                      <div key={idx} className="text-sm text-gray-600 bg-green-50 p-2 rounded">
                                        "{strength}"
                                      </div>
                                    ))}
            </div>
                                </div>
                              )}
                              
                              {staff.allImprovements.length > 0 && (
                                <div>
                                  <div className="text-sm font-medium text-green-700 mb-2">📈 Improvements:</div>
                                  <div className="space-y-1">
                                    {staff.allImprovements.map((improvement, idx) => (
                                      <div key={idx} className="text-sm text-gray-600 bg-green-50 p-2 rounded">
                                        "{improvement}"
                        </div>
                                    ))}
                          </div>
                          </div>
                              )}
                          </div>
                          </div>
                          ))
                        ) : (
                          <div className="text-center text-gray-500 py-8">
                            <div className="text-4xl mb-4">📊</div>
                            <p className="text-lg">No peer evaluations submitted yet</p>
                            <p className="text-sm text-gray-400 mt-2">Peer evaluations will appear here once submitted</p>
                        </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200 shadow-sm">
                      <h4 className="font-bold text-gray-600 mb-6 flex items-center text-lg">
                        <span className="mr-3 text-2xl">👥</span>
                        Peer Evaluations
                        <span className="ml-3 text-sm text-red-600 bg-red-100 px-2 py-1 rounded-full">
                          🔒 Leader Access Only
                        </span>
                      </h4>
                      <div className="text-center text-gray-500 py-8">
                        <div className="text-4xl mb-4">🔒</div>
                        <p className="text-lg">Access Restricted</p>
                        <p className="text-sm text-gray-400 mt-2">Only leaders can view peer evaluations</p>
                  </div>
                    </div>
                  )}

                  {/* Most Helpful Colleague */}
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-100 shadow-sm">
                    <h4 className="font-bold text-yellow-800 mb-6 flex items-center text-lg">
                      <span className="mr-3 text-2xl">🏆</span>
                      Most Helpful Colleague
                      <span className="ml-3 bg-yellow-200 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {staffFeedback.mostHelpful?.length || 0}
                      </span>
                    </h4>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {staffFeedback.mostHelpful && staffFeedback.mostHelpful.length > 0 ? (
                        (() => {
                          // 중복 투표 집계
                          const voteCount = {};
                          const reasons = {};
                          
                          staffFeedback.mostHelpful.forEach(feedback => {
                            const colleague = feedback.mostHelpfulColleague || 'Unknown';
                            if (!voteCount[colleague]) {
                              voteCount[colleague] = 0;
                              reasons[colleague] = [];
                            }
                            voteCount[colleague]++;
                            if (feedback.mostHelpfulReason) {
                              reasons[colleague].push(feedback.mostHelpfulReason);
                            }
                          });
                          
                          // 투표 수로 정렬
                          const sortedColleagues = Object.entries(voteCount)
                            .sort(([,a], [,b]) => b - a);
                          
                          return sortedColleagues.map(([colleague, count], index) => (
                            <div key={colleague} className="bg-white p-4 rounded-lg border border-yellow-100 shadow-sm">
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center">
                                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-3">
                                    {colleague.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <span className="font-bold text-gray-900 text-lg">
                                        {colleague}
                          </span>
                                      <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-sm font-semibold">
                                        {count} vote{count > 1 ? 's' : ''}
                                      </span>
                        </div>
                                    <div className="text-sm text-gray-500">
                                      {count === 1 ? 'Nominated once' : `Nominated ${count} times`}
                          </div>
                          </div>
                          </div>
                        </div>
                              
                              {/* Reasons */}
                              <div className="space-y-3">
                                <div>
                                  <div className="text-sm font-medium text-yellow-700 mb-2">
                                    💭 Reason{reasons[colleague].length > 1 ? 's' : ''}:
                      </div>
                                  <div className="space-y-2">
                                    {reasons[colleague].length > 0 ? (
                                      reasons[colleague].map((reason, reasonIndex) => (
                                        <div key={reasonIndex} className="text-sm text-gray-600 bg-yellow-50 p-3 rounded">
                                          "{reason}"
                    </div>
                                      ))
                                    ) : (
                                      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                                        No reasons provided
                  </div>
                                    )}
                </div>
                                </div>
                              </div>
                          </div>
                          ));
                        })()
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          <div className="text-4xl mb-4">🏆</div>
                          <p className="text-lg">No most helpful colleague nominations yet</p>
                          <p className="text-sm text-gray-400 mt-2">Nominations will appear here once submitted</p>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}



      </div>
      {/* Create New Feedback Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50" onClick={() => {
          setShowCreateModal(false);
          setNewFeedbackForm({ eventId: '', games: [] });
        }}>
          <div className="bg-white p-8 rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Create New Feedback Form</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Event</label>
                <select 
                  value={newFeedbackForm.eventId} 
                  onChange={(e) => setNewFeedbackForm(prev => ({ ...prev, eventId: e.target.value }))}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Choose an event...</option>
                  {allAvailableEvents.map(event => (
                    <option key={event.id || event.eventId} value={event.id || event.eventId}>
                      {event.title || event.eventTitle} ({event.branch})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Games</label>
                <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                  {allGames.map(game => (
                    <label key={game.gameId} className="flex items-center gap-2 p-1">
                      <input
                        type="checkbox"
                        checked={newFeedbackForm.games.includes(game.gameId)}
                        onChange={(e) => {
                          setNewFeedbackForm(prev => ({
                            ...prev,
                            games: e.target.checked 
                              ? [...prev.games, game.gameId]
                              : prev.games.filter(g => g !== game.gameId)
                          }));
                        }}
                      />
                      <span className="text-sm">{game.name} <span className="text-gray-500">({game.category})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleCreateFeedback} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Create
              </button>
              <button onClick={() => {
                setShowCreateModal(false);
                setNewFeedbackForm({ eventId: '', games: [] });
              }} className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 게임 상세 정보 모달 - 모든 탭 밖에 위치 */}
      {showGameDetails && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center" style={{ zIndex: 9999 }} onClick={() => setShowGameDetails(false)}>
          <div className="bg-white p-8 rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {allGames.find(game => game.gameId === selectedGame)?.name || selectedGame} - Detailed Feedback
              </h2>
              <button 
                onClick={() => setShowGameDetails(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            {gameDetails.length > 0 ? (
              <div>
                {/* 게임 평점 요약 */}
                <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
                  <div className="text-center">
                    <span className="text-sm text-gray-500">Average Game Rating:</span>
                    <div className="font-bold text-yellow-500 text-3xl">
                      {(gameDetails.reduce((sum, d) => sum + (d.rating || 0), 0) / gameDetails.length).toFixed(1)} ★
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Based on {gameDetails.length} feedback(s)
                    </div>
                  </div>
                </div>

                {/* Performance Analysis and Recommendations */}
                {(() => {
                  const avgRating = gameDetails.reduce((sum, d) => sum + (d.rating || 0), 0) / gameDetails.length;
                  const performance = avgRating >= 4.5 ? '🔥 Excellent' : avgRating >= 4.0 ? '👍 Good' : avgRating >= 3.5 ? '👌 Average' : '⚠️ Needs Improvement';
                  
                  const getConsiderationsAndRecommendations = (rating) => {
                    if (rating >= 4.5) {
                      return {
                        considerations: [
                          "Participants were highly satisfied with the game",
                          "Overwhelmingly positive feedback received",
                          "Custom Feedback primarily contained positive comments"
                        ],
                        recommendations: [
                          "Consider using this game as a core game in future events",
                          "Document the game's execution as a best practice",
                          "Apply this game's success factors to other games"
                        ]
                      };
                    } else if (rating >= 4.0) {
                      return {
                        considerations: [
                          "Generally satisfactory performance",
                          "Most participants were satisfied",
                          "Some minor improvement areas identified"
                        ],
                        recommendations: [
                          "Maintain the game with minor adjustments",
                          "Address specific improvement points from Custom Feedback",
                          "Consider optimizing game duration or group size"
                        ]
                      };
                    } else if (rating >= 3.5) {
                      return {
                        considerations: [
                          "Moderate satisfaction level",
                          "Mixed feedback from participants",
                          "Clear improvement areas identified"
                        ],
                        recommendations: [
                          "Hold a team meeting to analyze game issues",
                          "Review Custom Feedback for common complaints",
                          "Consider modifying game rules or execution method"
                        ]
                      };
                    } else {
                      return {
                        considerations: [
                          "Participants were dissatisfied with the game",
                          "Significant negative feedback received",
                          "Major issues with game execution or design"
                        ],
                        recommendations: [
                          "Immediately hold a team meeting to address issues",
                          "Consider replacing the game entirely",
                          "Analyze Custom Feedback for root cause analysis",
                          "Review if game matches participant group characteristics"
                        ]
                      };
                    }
                  };

                  const { considerations, recommendations } = getConsiderationsAndRecommendations(avgRating);
                  
                  return (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <span className="mr-2">📊</span>
                        Performance Analysis: {performance}
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-blue-700 mb-3 flex items-center">
                            <span className="mr-2">🔍</span>
                            Considerations:
                          </h4>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {considerations.map((item, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-blue-500 mr-2">•</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-green-700 mb-3 flex items-center">
                            <span className="mr-2">💡</span>
                            Recommendations:
                          </h4>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {recommendations.map((item, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-green-500 mr-2">•</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                    </div>
                    </div>
                  );
                })()}

                {/* Positive/Negative 피드백 그래프 */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4">Feedback Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Positive 피드백 */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-700 mb-3 flex items-center">
                        <span className="mr-2">👍</span>
                        Positive Feedback
                      </h4>
                      <div className="space-y-2">
                        {['Fun & engaging', 'Helped me connect', 'Right duration'].map(option => {
                          const count = gameDetails.filter(d => 
                            d.positiveOptions && d.positiveOptions.includes(option)
                          ).length;
                          const percentage = gameDetails.length > 0 ? (count / gameDetails.length) * 100 : 0;
                          return (
                            <div key={option} className="flex items-center justify-between">
                              <span className="text-sm text-green-800">{option}</span>
                              <div className="flex items-center">
                                <div className="w-24 bg-green-200 rounded-full h-2 mr-2">
                                  <div 
                                    className="bg-green-600 h-2 rounded-full" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-green-600 w-8">{count}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Negative 피드백 */}
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-medium text-red-700 mb-3 flex items-center">
                        <span className="mr-2">👎</span>
                        Negative Feedback
                      </h4>
                      <div className="space-y-2">
                        {['Boring', 'Group size issue (too many/few)', 'Confusing / unclear'].map(option => {
                          const count = gameDetails.filter(d => 
                            d.negativeOptions && d.negativeOptions.includes(option)
                          ).length;
                          const percentage = gameDetails.length > 0 ? (count / gameDetails.length) * 100 : 0;
                          return (
                            <div key={option} className="flex items-center justify-between">
                              <span className="text-sm text-red-800">{option}</span>
                              <div className="flex items-center">
                                <div className="w-24 bg-red-200 rounded-full h-2 mr-2">
                                  <div 
                                    className="bg-red-600 h-2 rounded-full" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-red-600 w-8">{count}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Add your own feedback 섹션 */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4">Custom Feedback</h3>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {gameDetails
                      .filter(d => d.customFeedback && d.customFeedback.trim())
                      .map((detail, index) => (
                      <div key={detail.id} className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-700 italic">"{detail.customFeedback}"</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Submitted by: {detail.submittedBy} | {new Date(detail.createdAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                    {gameDetails.filter(d => d.customFeedback && d.customFeedback.trim()).length === 0 && (
                      <div className="text-center text-gray-500 py-4">
                        No custom feedback provided
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No detailed feedback available for this game.
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
      </div>
    </div>
  );
}

